<template>
  <div class="tc-video-player-box" :style="{ transform: `scale(${autoScale ? screenScale : 1})` }">
    <!-- Loading overlay: replaces only .video-wrapper slot when player not ready -->
    <div class="video-wrapper" v-show="!isMatchDayEnding && streamUrl && !isWebRTCStream && !videoPlayerReady">
      <VideoLoadingOverlay variant="placeholder" />
    </div>
    <div class="video-wrapper" v-show="!isMatchDayEnding && streamUrl && !isWebRTCStream && videoPlayerReady" ref="videoPlayerContainer">
      <!-- VePlayer decodes the source off-screen; frames are composited into videoCanvas. -->
      <div :id="veCanvasContainerId" ref="vePlayerCanvasContainer" class="ve-player-canvas-source"></div>

      <!-- Canvas for compositing -->
      <canvas
        ref="videoCanvas"
        class="video-canvas"
        style="display: none"
        @click="handleCanvasClick"
        @touchstart.passive="handleTouchStart"
        @touchmove="handleTouchMove"
        :class="{ isLarger: screenScale > 1, 'ios-canvas': isIOSDevice, 'android-canvas': isAndroidDevice }"
      ></canvas>

      <!-- Play/Pause Controls - Always visible when paused or when showControls is true -->
      <div
        class="video-controls"
        :class="{ 'controls-visible': showControls || isPaused }"
        v-if="!isReinitializing"
        @click.stop="handleControlsClick"
      >
        <!-- Volume Control -->
        <div class="volume-control" @mouseenter="showVolumeSlider = true" @mouseleave="showVolumeSlider = false">
          <!-- Volume Slider (hidden on iOS) - positioned to the left of button -->
          <div class="volume-slider-container" v-show="showVolumeSlider && !isIOSDevice" @click.stop>
            <input
              type="range"
              min="0"
              max="100"
              v-model="volume"
              @input="updateVolume"
              class="volume-slider"
              :title="`Volume: ${volume}%`"
            />
            <span class="volume-value">{{ volume }}%</span>
          </div>
          <button @click.stop="handleVolumeClick" class="control-btn volume-btn" :title="volumeTitle">
            <!-- Muted Icon -->
            <svg v-if="volume === 0" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
              />
            </svg>
            <!-- High Volume Icon -->
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
              />
            </svg>
          </button>
        </div>
        <!-- Play button when paused -->
        <button v-if="isPaused" @click.stop="handlePlayClick" class="control-btn play-btn" title="Play">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>
        <!-- Pause button when playing -->
        <button v-else @click.stop="handlePauseClick" class="control-btn pause-btn" title="Pause">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        </button>
      </div>

      <!-- Dimmed overlay when paused (click anywhere to resume) -->
      <div class="video-paused-overlay" v-if="isPaused && !isReinitializing" @click.stop="handlePlayClick"></div>
      <!-- Over the video, e.g. the bet placed / cancelled pill (video-toast.vue). -->
      <slot name="overlay" />
    </div>

    <!-- WebRTC stream not supported by this player -->
    <div v-if="isWebRTCStream" class="no-active-arena">
      <img class="logo" :src="require('@/assets/images/alert.webp')" />
      <div class="text">
        {{ $t('Stream not available') }}
      </div>
    </div>

    <div v-if="!streamUrl" class="no-active-arena">
      <img class="logo" v-if="!streamError" :src="require('@/assets/images/alert.webp')" />
      <img class="logo-out-of-balance" v-else :src="require('@/assets/images/out-of-money.png')" />
      <template v-if="streamError">
        <div class="text my-3">
          {{ $t('Stream locked') }}
        </div>
        <div class="text">
          {{ convertStreamErrorMessage(streamError) }}
        </div>
      </template>
      <template v-else>
        <div class="text">
          {{ $t('No fight at the moment') }}
        </div>
        <div class="text">
          {{ $t('Please come back again') }}
        </div>
      </template>
    </div>
    <div v-if="isMatchDayEnding" class="no-active-arena match-day-ending">
      <img class="logo" :src="require('@/assets/images/alert.webp')" />
      <div class="text">
          {{ $t('This arena has ended') }}
      </div>
      <div class="text">
          {{ $t('Thank you for your support') }}
      </div>
    </div>
    <div v-if="!embedded" :class="['video-banner-wrapper', { 'is-mobile': isMobile, reverse: isReverse }]">
      <div class="odds-wrapper">
        <span>{{ $t('Banker') }}</span>
        <span v-if="autoScale && screenScale > 1" class="ml-16 odds">{{ bankerOdds }}</span>
      </div>
      <div class="odds-wrapper">
        <span v-if="autoScale && screenScale > 1" class="mr-16 odds">{{ playerOdds }}</span>
        <span>{{ $t('Player') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { FightStatus } from '@/utils/index.model';
import { isMobile, isIOS, isAndroid, isWindows } from 'mobile-device-detect';
import { find, isEmpty } from 'lodash';
import {
  getImgUrl,
  normalizeStreamUrl,
  getBaseUrl,
  isProdMode,
  getArenaStreamDrmType,
} from '@/utils/helpers';
import { getVePlayerLicense } from '@/config/ve-player-license';
import { WatermarkConfigManager as _SysIntegrity } from '@/utils/watermark-config-manager';
import { AntiPipLayer, createAntiPipLayer } from '@/utils/anti-pip';
import { createRobustWatermark } from '@/utils/robust-watermark';
import VideoLoadingOverlay from '@/components/video-loading-overlay.vue';

export default {
  components: { VideoLoadingOverlay },
  props: {
    // Set by a desktop pane so this player follows its own arena's stream
    // instead of the single most-recent global one.
    arenaId: {
      type: String,
      default: '',
    },
    /*
     * Rendered inside a desktop pane, where the Banker-vs-Player banner belongs
     * to the pane chrome rather than the player. Matches ve-player's prop of the
     * same name; default false leaves mobile untouched.
     */
    embedded: {
      type: Boolean,
      default: false,
    },
    isAnyPopupOpen: {
      type: Boolean,
      default: false,
    },
    showOverlayControls: {
      type: Boolean,
      default: true,
    },
    videoPlayerReady: {
      type: Boolean,
      default: false,
    },
    videoPlayerReady: {
      type: Boolean,
      default: false,
    },
    videoPlayerReady: {
      type: Boolean,
      default: false,
    },
    videoPlayerReady: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // DOM ids of this player's off-screen source video and VePlayer container.
      // Per instance: two panes can each run one, and `document.getElementById` /
      // the cleanups below must never reach the other pane's elements -- sharing
      // one id made the second player remove the first's video, which then stayed
      // dead after switching from single to split view on a cockfight arena.
      canvasPlayerId: `videoPlayerCanvasId-${this._uid}`,
      veCanvasContainerId: `ve-player-canvas-container-${this._uid}`,
      screenScale: 1,
      isMobile,

      // Canvas and video
      canvas: null,
      ctx: null,
      sourceVideo: null,
      outputVideo: null,
      vePlayer: null,
      tcPlayer: null,
      usingTcPlayerFallback: false,
      vePlayerGeneration: 0,
      licenseConfigured: false,
      canvasStream: null,
      overlays: [],
      animationId: null,
      pipEnabled: true,
      canvasReady: false,

      // Performance optimization - target 30fps for consistency across all devices
      targetFPS: 30,
      frameInterval: 1000 / 30,
      lastFrameTime: 0,

      // Anti-PiP layer (initialized in mounted if pip=false)
      antiPipLayer: null,

      // Feature detection
      supportsCanvasCapture: false,
      useDirectMode: false,
      watermarkElement: null,

      _intMgr: null,
      _sysEn: true,
      _l1En: true,

      // Robust anti-piracy watermark
      robustWatermark: null,
      robustWatermarkEnabled: true,

      // Play/Pause control
      isPaused: false,
      isReinitializing: false,

      // Volume control
      volume: 0, // Volume level 0-100 (starts at 0/muted)
      previousVolume: 50, // Store previous volume for unmute
      showVolumeSlider: false, // Show/hide volume slider on hover

      // Stream refresh control
      lastPlayTime: null,
      pauseStartTime: null,
      refreshThreshold: 3000, // 3 seconds threshold
      pauseDuration: 0,
      skipNextPlayCheck: false, // Flag to skip reinitialize check after recreation

      // Page visibility tracking (for browser tab/app switch)
      pageHiddenTime: null,
      visibilityThreshold: 1000, // 1 second threshold for reinit on return
      seekVisibilityThreshold: 5000, // 5 seconds - only seek when hidden this long (avoids seek on pull-to-refresh)

      // Auto-sync to live edge - checks periodically for drift
      autoSyncStartTimer: null,
      autoSyncInterval: null,
      autoSyncThreshold: 5, // seconds behind live edge before auto-seeking
      seekBufferSeconds: 3, // seek this far back from live edge to avoid buffer thrashing
      lastSyncCheck: 0,

      // Controls visibility
      showControls: false,
      controlsTimer: null,
      controlsAutoHideDelay: 7000, // 7 seconds auto-hide

      // Touch tracking for pull-to-refresh prevention
      touchStartY: null,

      // Watermark frequency control
      watermarkVisible: false,
      watermarkTimer: null,
      currentCorner: 'top-left', // For movement type 3: tracks which diagonal path (top or bottom)
      cornerAnimationPhase: 'start', // 'start' or 'end' - tracks position within the diagonal path
      frequencyConfig: {
        0: { mode: 'off' },
        1: { mean_on_s: 2.4, mean_off_s: 21.6 },
        2: { mean_on_s: 2.8, mean_off_s: 11.2 },
        3: { mean_on_s: 3.2, mean_off_s: 7.47 },
        4: { mean_on_s: 3.6, mean_off_s: 5.4 },
        5: { mean_on_s: 4.0, mean_off_s: 4.0 },
        6: { mean_on_s: 4.4, mean_off_s: 2.93 },
        7: { mean_on_s: 4.8, mean_off_s: 2.06 },
        8: { mean_on_s: 5.2, mean_off_s: 1.3 },
        9: { mean_on_s: 5.6, mean_off_s: 0.62 },
        10: { mode: 'always_on' },
      },

      // Error retry control
      retryTimer: null,
      retryCount: 0,
      maxRetries: 10, // 3 minutes total (36 * 5 seconds)
      isRetrying: false,
      retryErrorMessage: '',
    };
  },

  mounted() {
    // Initialize Anti-PiP layer (if pip=false in URL or store)
    this.antiPipLayer = createAntiPipLayer(this.pipDisabled, (details) => {});

    // On iOS, this component may never mount (V1 is shown first); if it does mount with arena already set, request stream
    if (this.arena.id) {
      this.getArenaStreamUrl(this.arena.id);
    }

    // Always use canvas for watermark manipulation
    // The difference: Desktop uses captureStream, iOS displays canvas directly
    this.initCanvas();

    const waitForCanvas = () => {
      if (this.canvasReady && this.canvas) {
        this.addText();
        this.startWatermarkFrequency();
        this._initSysIntegrity();
      } else {
        setTimeout(waitForCanvas, 50);
      }
    };

    // Use minimal delay for first load - just enough for DOM readiness
    // The stream URL is now requested early from cock-fight.vue, so it may already be available
    this.$nextTick(() => {
      this.startPlay();
      waitForCanvas();
    });

    // Add visibility change listener for browser tab/app switch detection
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Start auto-sync interval after a delay to allow video to load first
    // Checks every 10 seconds if we've drifted too far behind
    // Kept so beforeDestroy can cancel it: a player torn down inside the first
    // 15s (arena switch, view toggle) otherwise started an interval after its
    // teardown that nothing ever cleared.
    this.autoSyncStartTimer = setTimeout(() => {
      this.autoSyncInterval = setInterval(() => {
        this.checkAndSyncToLive();
      }, 10000);
    }, 15000); // Wait 15 seconds before starting auto-sync
  },

  computed: {
    ...mapGetters('fight', ['fight']),
    ...mapGetters({
      selectedArena: 'general/selectedArena',
      arenas: 'general/arenas',
      autoScale: 'general/videoAutoScale',
      config: 'general/config',
      globalStreamUrl: 'general/streamUrl',
      globalStreamWatermarkData: 'general/streamWatermarkData',
      arenaStream: 'general/arenaStream',
      globalStreamDrmData: 'general/streamDrmData',
      member: 'general/member',
      streamError: 'general/streamError',
      pipDisabled: 'general/pipDisabled',
    }),
    /*
     * The arena this player is for. Almost always the selected arena -- and in a
     * cockfight pane its scoped one. A player given an `arenaId` for a different
     * arena (the Tài Xỉu pane's table, whose arena is never the root selection)
     * takes that arena from the list instead, so it asks for the right stream and
     * reads the right arena's status rather than whatever is selected.
     */
    arena() {
      const selected = this.selectedArena;
      if (!this.arenaId || (selected && selected.id === this.arenaId)) return selected;
      return (this.arenas || []).find((item) => item.id === this.arenaId) || { id: this.arenaId };
    },
    streamArenaId() {
      return this.arenaId || (this.arena && this.arena.id) || '';
    },
    /*
     * Prefer this player's own arena stream when one exists, so the desktop
     * split view can run two players at once; fall back to the global for every
     * existing caller (mobile, v1 desktop), leaving their behaviour unchanged.
     */
    ownArenaStream() {
      return this.arenaStream(this.streamArenaId);
    },
    streamUrl() {
      /*
       * An explicit arenaId means this player owns one specific arena, so it
       * must never fall back to the shared "most recent response" global -- on
       * first load that is whichever arena replied last, which showed both
       * desktop panes the same feed. Empty until this arena's own reply lands.
       */
      if (this.arenaId) return (this.ownArenaStream && this.ownArenaStream.url) || '';
      // No arenaId (the /v1 page): prefer this arena's own reply, so a reply for
      // another arena landing in the shared global cannot switch the channel.
      // The global is only the fallback until this arena's reply arrives.
      return (this.ownArenaStream && this.ownArenaStream.url) || this.globalStreamUrl;
    },
    streamWatermarkData() {
      if (this.arenaId) return this.ownArenaStream || {};
      if (this.ownArenaStream && this.ownArenaStream.url) return this.ownArenaStream;
      return this.globalStreamWatermarkData;
    },
    streamDrmData() {
      if (this.arenaId) return this.ownArenaStream || {};
      if (this.ownArenaStream && this.ownArenaStream.url) return this.ownArenaStream;
      return this.globalStreamDrmData;
    },
    memberName() {
      return this.member.displayName;
    },
    isReverse() {
      return this.arena.isReverse;
    },
    bankerOdds() {
      const banker = find(this.fight.selections, (s) => s.name === 'banker');
      return banker && !isEmpty(banker) ? `${banker.odds}x` : '';
    },
    playerOdds() {
      const player = find(this.fight.selections, (s) => s.name === 'player');
      return player && !isEmpty(player) ? `${player.odds}x` : '';
    },
    isMobileLandscape() {
      return isMobile && this.$screen.width > this.$screen.height;
    },
    isIOSDevice() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    },
    isAndroidDevice() {
      return /Android/i.test(navigator.userAgent);
    },
    volumeTitle() {
      return this.volume === 0 ? 'Unmute' : `Volume: ${this.volume}%`;
    },
    isPipDisabled() {
      return AntiPipLayer.isPipDisabled(this.pipDisabled);
    },
    isWebRTCStream() {
      return (
        this.streamUrl &&
        (this.streamUrl.startsWith('webrtc://') ||
          this.streamUrl.includes('/whep/') ||
          this.streamUrl.includes('/whip-play/')) && !this.isMatchDayEnding
      );
    },
    isMatchDayEnding() {
      return this.arena.isMatchDayEnding;
    },
  },

  methods: {
    ...mapActions('general', ['updateGeneral', 'getArenas', 'getArenaStreamUrl']),
    ...mapActions('fight', ['resetFight', 'resetFights', 'resetFightsPage']),
    isEmpty,
    getImgUrl,

    // Detect if canvas.captureStream() is supported
    detectCanvasSupport() {
      try {
        const testCanvas = document.createElement('canvas');
        this.supportsCanvasCapture = typeof testCanvas.captureStream === 'function';
      } catch (e) {
        this.supportsCanvasCapture = false;
      }
    },

    // Create output video element for canvas stream (Desktop only)
    createOutputVideo() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isAndroid = /Android/i.test(navigator.userAgent);

      // iOS/Android: Display canvas directly, no output video needed
      if (isIOS || isAndroid) {
        return;
      }

      // Desktop: Create output video from canvas stream
      // Start muted - updateVolume() will apply persisted state after play succeeds
      this.outputVideo = document.createElement('video');
      this.outputVideo.id = 'outputVideo';
      this.outputVideo.autoplay = true;
      this.outputVideo.muted = true;
      this.outputVideo.volume = this.volume / 100;
      this.outputVideo.playsInline = true;
      this.outputVideo.style.width = '100%';
      this.outputVideo.style.height = '100%';
      this.outputVideo.style.position = 'absolute';
      this.outputVideo.style.top = '0';
      this.outputVideo.style.left = '0';
      this.outputVideo.style.zIndex = '1';

      // Apply PiP disable if needed
      if (this.antiPipLayer) {
        this.antiPipLayer.protectVideoElement(this.outputVideo);
      }

      // Replace canvas with output video in the container
      const container = this.$refs.videoPlayerContainer;
      if (container) {
        container.appendChild(this.outputVideo);
      }
    },

    // Initialize canvas
    initCanvas() {
      this.$nextTick(() => {
        this.canvas = this.$refs.videoCanvas;
        if (this.canvas) {
          // Get standard 2d context (avoid desynchronized which causes flashing on low-end devices)
          this.ctx = this.canvas.getContext('2d');

          // Disable image smoothing for crisp watermark rendering
          this.ctx.imageSmoothingEnabled = false;

          // Use cached device detection from mobile-device-detect (avoids re-parsing user agent)
          if (isIOS || isAndroid) {
            // iOS/Android: Display canvas directly (not hidden)
            // This avoids the expensive captureStream pipeline on mobile
            this.canvas.style.display = 'block';
            this.canvas.style.position = 'absolute';
            this.canvas.style.top = '0';
            this.canvas.style.left = '0';
            this.canvas.style.width = '100%';
            this.canvas.style.height = '100%';
            this.canvas.style.zIndex = '1';
          } else {
            // Desktop: Create output video from canvas stream
            this.createOutputVideo();
          }

          this.startRenderLoop();
          this.canvasReady = true;
        }
      });
    },

    // Initialize the robust anti-piracy watermark system
    // initRobustWatermark() {
    //   if (!this.canvas) return;

    //   // Check watermark type - only enable robust watermark for types 3 and 4
    //   const watermarkType = (this.streamWatermarkData && this.streamWatermarkData.watermarkType) || 1;

    //   if (watermarkType === 3 || watermarkType === 4) {
    //     // For types 3 and 4, use username from streamWatermarkData.watermark
    //     const username =
    //       (this.streamWatermarkData && this.streamWatermarkData.watermark) ||
    //       this.memberName ||
    //       this.member.displayName ||
    //       'USER';

    //     // Use 'subtle' preset - can be changed to 'subtle' or 'strong'
    //     this.robustWatermark = createRobustWatermark(username, 'subtle');
    //     this.robustWatermark.initialize(this.canvas);
    //     this.robustWatermarkEnabled = true;
    //     this.robustWatermark.setEnabled(true);
    //   } else {
    //     // For types 1 and 2, disable robust watermark
    //     this.robustWatermarkEnabled = false;
    //     if (this.robustWatermark) {
    //       this.robustWatermark.setEnabled(false);
    //     }
    //   }
    // },

    // Toggle robust watermark on/off
    // toggleRobustWatermark(enabled) {
    //   this.robustWatermarkEnabled = enabled !== undefined ? enabled : !this.robustWatermarkEnabled;

    //   if (this.robustWatermark) {
    //     this.robustWatermark.setEnabled(this.robustWatermarkEnabled);
    //   } else if (this.robustWatermarkEnabled && this.canvas) {
    //     // Initialize if not yet created
    //     this.initRobustWatermark();
    //   }
    // },

    // // Update robust watermark configuration
    // updateRobustWatermarkConfig(config) {
    //   if (this.robustWatermark) {
    //     this.robustWatermark.updateConfig(config);
    //   }
    // },

    // // Change robust watermark preset (subtle, moderate, strong)
    // setRobustWatermarkPreset(preset) {
    //   if (!this.canvas) return;

    //   const username = this.memberName || this.member.displayName || 'USER';
    //   this.robustWatermark = createRobustWatermark(username, preset);
    //   this.robustWatermark.initialize(this.canvas);
    // },

    configureVePlayerLicense() {
      if (this.licenseConfigured || typeof window.VePlayer === 'undefined') return;
      window.VePlayer.live.setLicenseConfig({ license: getVePlayerLicense() });
      this.licenseConfigured = true;
    },

    inferVeStreamProtocol(url) {
      if (!url) return null;
      const value = String(url).toLowerCase();
      if (/\.sdp(\?|$)/.test(value) || value.includes('webrtm')) return 'rtm';
      if (/\.flv(\?|$)/.test(value)) return 'flv';
      if (/\.m3u8(\?|$)/.test(value)) return 'hls';
      if (/\.mpd(\?|$)/.test(value)) return 'dash';
      return null;
    },

    buildVePlaylist(url) {
      const primaryProtocol = this.inferVeStreamProtocol(url);
      let flvUrl = url;
      let hlsUrl = url;

      if (primaryProtocol === 'hls') {
        flvUrl = url.replace(/\.m3u8(\?.*)?$/i, '.flv');
      } else if (primaryProtocol === 'flv') {
        hlsUrl = url.replace(/\.flv(\?.*)?$/i, '.m3u8');
      } else if (primaryProtocol === 'rtm') {
        const rtmSuffix = /_webrtm\.sdp/i.test(url) ? /_webrtm\.sdp(\?.*)?$/i : /\.sdp(\?.*)?$/i;
        flvUrl = url.replace(rtmSuffix, '.flv');
        hlsUrl = url.replace(rtmSuffix, '.m3u8');
      }

      const definitions = [];
      const add = (value) => {
        if (value && definitions.indexOf(value) === -1) definitions.push(value);
      };

      add(url);
      if (primaryProtocol === 'rtm' || primaryProtocol === 'hls') add(flvUrl);
      if (primaryProtocol === 'rtm' || primaryProtocol === 'flv') add(hlsUrl);

      const protocols = definitions.map((value) => this.inferVeStreamProtocol(value)).filter(Boolean);
      const uniqueProtocols = [...new Set(protocols)];
      const primary = primaryProtocol || uniqueProtocols[0] || 'hls';
      return {
        definitions,
        fallback: { fallbackOrder: [primary, ...uniqueProtocols.filter((protocol) => protocol !== primary)] },
      };
    },

    normalizeSignedDrmUrl(rawUrl) {
      if (!rawUrl) return '';
      const value = String(rawUrl).trim();
      if (!value) return '';
      if (/^https?:\/\//i.test(value)) return value;
      if (/^\/\//.test(value)) return `https:${value}`;
      const query = value.startsWith('?') ? value.slice(1) : value;
      if (/(?:^|&)x-(?:algorithm|signature|credential)=/i.test(query)) {
        return `https://open.byteplusapi.com/?${query}`;
      }
      return value;
    },

    extractSignedDrmHeaders(rawUrl) {
      if (!rawUrl) return {};
      try {
        const parsed = new URL(this.normalizeSignedDrmUrl(rawUrl));
        const query = {};
        parsed.searchParams.forEach((value, key) => {
          query[key.toLowerCase()] = value;
        });
        const algorithm = query['x-algorithm'];
        const credential = query['x-credential'];
        const signedHeaders = query['x-signedheaders'];
        const signature = query['x-signature'];
        if (!algorithm || !credential || !signedHeaders || !signature) return {};
        const headers = {
          Authorization: `${algorithm} Credential=${credential}, SignedHeaders=${signedHeaders}, Signature=${signature}`,
          'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
        };
        if (query['x-date']) headers['X-Date'] = query['x-date'];
        if (query['x-content-sha256']) headers['X-Content-Sha256'] = query['x-content-sha256'];
        return headers;
      } catch (e) {
        return {};
      }
    },

    buildVeDrmConfig() {
      const licenseUrl = this.normalizeSignedDrmUrl(this.streamDrmData && this.streamDrmData.drmLicenseUrl);
      const certUrl = this.normalizeSignedDrmUrl(this.streamDrmData && this.streamDrmData.drmCertUrl);
      if (!licenseUrl) return {};

      if (getArenaStreamDrmType() === 'fairplay') {
        if (!certUrl) return {};
        return {
          fairplay: {
            getDrmConfig: async () => {
              const certHeaders = this.extractSignedDrmHeaders(certUrl);
              const headers = this.extractSignedDrmHeaders(licenseUrl);
              return {
                serverCertificatePath: certUrl,
                serverProcessSPCPath: licenseUrl,
                ...(Object.keys(certHeaders).length ? { serverCertificateHeaders: certHeaders } : {}),
                ...(Object.keys(headers).length ? { serverProcessSPCHeaders: headers, headers } : {}),
              };
            },
          },
        };
      }

      return {
        widevine: {
          getDrmConfig: async () => {
            const headers = this.extractSignedDrmHeaders(licenseUrl);
            return {
              serverProcessSPCPath: licenseUrl,
              ...(Object.keys(headers).length ? { serverProcessSPCHeaders: headers, headers } : {}),
            };
          },
        },
      };
    },

    buildCanvasVePlayerConfig(url) {
      const { definitions, fallback } = this.buildVePlaylist(url);
      const config = {
        id: this.veCanvasContainerId,
        width: '100%',
        height: '100%',
        autoplay: {
          muted: true,
          enableDegradeMuteAutoplay: true,
          enableUserActionAutoplay: true,
        },
        ignores: ['loading', 'start'],
        isLive: true,
        controls: false,
        playsinline: true,
        videoInit: true,
        closeResizeObserver: true,
        pip: false,
        fullscreen: false,
        cssFullscreen: false,
        screenShot: false,
        download: false,
        rotate: false,
        playbackRate: false,
        mini: false,
        miniprogress: false,
        keyShortcut: false,
        enableContextmenu: false,
        closeVideoClick: true,
        closeVideoTouch: true,
        closeVideoDblclick: true,
        videoAttributes: {
          playsinline: true,
          'webkit-playsinline': true,
          'x5-video-player-type': 'h5',
          'x5-video-player-fullscreen': true,
          crossorigin: 'anonymous',
        },
        logger: { appId: '1017733' },
        flvLowLatency: {
          enable: true,
          speedUpThreshold: 1.5,
          speedUpRate: 1.1,
          slowDownThreshold: 0.5,
          slowDownRate: 0.95,
        },
        error: { retryCount: 5, retryInterval: 3000 },
        playlist: [{ definitions }],
        fallback,
      };
      const drm = this.buildVeDrmConfig();
      if (Object.keys(drm).length) config.drm = drm;
      return config;
    },

    refreshVeSourceVideo() {
      const container = this.$refs.vePlayerCanvasContainer;
      const video = container && container.querySelector('video');
      if (!video) return null;
      this.sourceVideo = video;
      video.playsInline = true;
      if (this.antiPipLayer) this.antiPipLayer.protectVideoElement(video);
      this.updateCanvasSize();
      return video;
    },

    destroyVePlayer() {
      this.vePlayerGeneration++;
      const hadVePlayer = !!this.vePlayer;
      if (this.vePlayer) {
        try {
          this.vePlayer.destroy();
        } catch (e) {
          console.warn('[tc-video] Error destroying VePlayer:', e);
        }
        this.vePlayer = null;
      }
      if (hadVePlayer) this.sourceVideo = null;
      const container = this.$refs.vePlayerCanvasContainer;
      if (container) container.innerHTML = '';
    },

    destroyTcPlayer() {
      if (this.tcPlayer) {
        try {
          this.tcPlayer.dispose();
        } catch (e) {
          console.warn('[tc-video] Error disposing TCPlayer fallback:', e);
        }
        this.tcPlayer = null;
      }

      const fallbackVideos = document.querySelectorAll(`#${this.canvasPlayerId}, #${this.canvasPlayerId}_html5_api`);
      fallbackVideos.forEach((element) => {
        try {
          if (element.tagName === 'VIDEO') {
            element.pause();
            element.removeAttribute('src');
            element.load();
          }
          element.remove();
        } catch (e) {
          // Ignore fallback video cleanup errors.
        }
      });
      this.sourceVideo = null;
    },

    getTcPlayerFallbackUrl(url) {
      if (/\.flv(\?|$)/i.test(url)) return url.replace(/\.flv(?=\?|$)/i, '.m3u8');
      if (/_webrtm\.sdp(\?|$)/i.test(url)) return url.replace(/_webrtm\.sdp(?=\?|$)/i, '.m3u8');
      if (/\.sdp(\?|$)/i.test(url)) return url.replace(/\.sdp(?=\?|$)/i, '.m3u8');
      return url;
    },

    refreshTcSourceVideo() {
      const video =
        document.getElementById(`${this.canvasPlayerId}_html5_api`) ||
        document.querySelector(`#${this.canvasPlayerId} video`) ||
        document.getElementById(this.canvasPlayerId);
      if (!video || video.tagName !== 'VIDEO') return null;
      this.sourceVideo = video;
      video.playsInline = true;
      if (this.antiPipLayer) this.antiPipLayer.protectVideoElement(video);
      this.updateCanvasSize();
      return video;
    },

    startPlayTcPlayer(url, useCanvasCapture) {
      if (typeof TCPlayer === 'undefined') {
        console.error('[tc-video] TCPlayer fallback SDK not loaded');
        this.scheduleRetry();
        return;
      }

      this.cleanupCanvasStream();
      this.destroyVePlayer();
      this.destroyTcPlayer();
      this.usingTcPlayerFallback = true;

      const video = document.createElement('video');
      video.id = this.canvasPlayerId;
      video.className = 'video-js';
      video.style.position = 'fixed';
      video.style.top = '0';
      video.style.left = '0';
      video.style.width = '2px';
      video.style.height = '2px';
      video.style.opacity = '0';
      video.style.pointerEvents = 'none';
      video.style.zIndex = '-1';
      video.muted = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('playsinline', '');
      if (this.antiPipLayer) this.antiPipLayer.protectVideoElement(video);
      document.body.appendChild(video);

      const fallbackUrl = this.getTcPlayerFallbackUrl(url);
      try {
        this.tcPlayer = TCPlayer(this.canvasPlayerId, {
          width: '100%',
          aspectRatio: '16:9',
          muted: true,
          autoplay: true,
          controls: false,
          isLive: true,
          playsinline: true,
          sources: [{ src: fallbackUrl, type: 'application/x-mpegURL' }],
        });

        let canvasStreamReady = false;
        const onReady = () => {
          const source = this.refreshTcSourceVideo();
          if (!source) return;
          if (!canvasStreamReady && useCanvasCapture) {
            this.setupCanvasStream();
            canvasStreamReady = true;
          }
          this.stopRetry();
        };

        this.tcPlayer.one('loadedmetadata', onReady);
        this.tcPlayer.one('loadeddata', onReady);
        this.tcPlayer.one('canplay', onReady);
        this.tcPlayer.one('playing', () => {
          onReady();
          this.updateVolume();
        });
        this.tcPlayer.on('error', () => this.scheduleRetry());
        this.tcPlayer.on('stalled', () => this.scheduleRetry());

        const playPromise = this.tcPlayer.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch((error) => console.warn('[tc-video] TCPlayer fallback play failed:', error));
        }
      } catch (error) {
        console.error('[tc-video] TCPlayer fallback initialization error:', error);
        this.destroyTcPlayer();
        this.scheduleRetry();
      }
    },

    activateTcPlayerFallback(url, useCanvasCapture) {
      if (this.usingTcPlayerFallback && this.tcPlayer) return;
      console.warn('[tc-video] VePlayer failed; switching canvas source to TCPlayer');
      this.stopRetry();
      this.startPlayTcPlayer(this.getTcPlayerFallbackUrl(url), useCanvasCapture);
    },

    async startPlayVePlayer(url, useCanvasCapture) {
      if (typeof window.VePlayer === 'undefined') {
        console.error('[tc-video] VePlayer SDK not loaded');
        this.activateTcPlayerFallback(url, useCanvasCapture);
        return;
      }

      this.cleanupCanvasStream();
      this.destroyTcPlayer();
      this.usingTcPlayerFallback = false;
      this.destroyVePlayer();
      const generation = this.vePlayerGeneration;
      this.configureVePlayerLicense();

      try {
        const player = await window.VePlayer.createLivePlayer(this.buildCanvasVePlayerConfig(url));
        if (generation !== this.vePlayerGeneration) {
          try {
            player.destroy();
          } catch (e) {
            // Ignore stale player cleanup errors.
          }
          return;
        }
        this.vePlayer = player;

        let canvasStreamReady = false;
        let sourceLookupAttempts = 0;
        const onReady = (mediaReady = true) => {
          if (generation !== this.vePlayerGeneration) return;
          const video = this.refreshVeSourceVideo();
          if (!video) {
            if (sourceLookupAttempts++ < 20) setTimeout(() => onReady(mediaReady), 50);
            return;
          }
          const canInitializeCanvasStream = mediaReady || video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
          if (canInitializeCanvasStream && !canvasStreamReady && useCanvasCapture) {
            this.setupCanvasStream();
            canvasStreamReady = true;
          }
          if (canInitializeCanvasStream) this.stopRetry();
        };

        this.vePlayer.once('loadeddata', onReady);
        this.vePlayer.once('canplay', onReady);
        this.vePlayer.once('playing', () => {
          onReady();
          this.updateVolume();
        });
        this.vePlayer.on('error', () => this.scheduleRetry());

        const LiveEvents = window.VePlayer.live && window.VePlayer.live.Events;
        if (LiveEvents && LiveEvents.FALLBACK_ERROR) {
          this.vePlayer.on(LiveEvents.FALLBACK_ERROR, () => {
            this.activateTcPlayerFallback(url, useCanvasCapture);
          });
        }

        // VePlayer may create its video synchronously before media events fire.
        this.$nextTick(() => onReady(false));
      } catch (error) {
        console.error('[tc-video] VePlayer initialization error:', error);
        this.destroyVePlayer();
        this.activateTcPlayerFallback(url, useCanvasCapture);
      }
    },

    // Create video elements programmatically
    startPlay() {
      if (!this.streamUrl) return;

      // The canvas path does not render WebRTC directly.
      if (this.isWebRTCStream) {
        return;
      }

      // Normalize URL for iOS devices
      const normalizedUrl = normalizeStreamUrl(this.streamUrl);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isAndroid = /Android/i.test(navigator.userAgent);

      if (isIOS) {
        // iOS: Hidden video with native HLS → draws to visible canvas
        this.startPlayIOS(normalizedUrl);
      } else if (isAndroid) {
        // Android: VePlayer source → visible canvas (no captureStream).
        // This avoids the expensive double-encoding pipeline
        this.startPlayAndroid(normalizedUrl);
      } else {
        // Desktop: VePlayer source → canvas → captureStream output.
        this.startPlayCanvas(normalizedUrl);
      }
    },

    // iOS playback: Hidden video with native HLS → Canvas (visible with watermarks)
    startPlayIOS(normalizedUrl) {
      // Release old source video decoder properly before creating a new one
      if (this.sourceVideo) {
        try {
          this.sourceVideo.pause();
          this.sourceVideo.removeAttribute('src');
          this.sourceVideo.load(); // Release hardware decoder
        } catch (e) {
          // Ignore
        }
        if (this.sourceVideo.parentNode) {
          this.sourceVideo.parentNode.removeChild(this.sourceVideo);
        }
        this.sourceVideo = null;
      }

      // Also clean up any orphaned video elements with the same ID (from previous component instances)
      const staleVideo = document.getElementById(this.canvasPlayerId);
      if (staleVideo) {
        try {
          if (staleVideo.tagName === 'VIDEO') {
            staleVideo.pause();
            staleVideo.removeAttribute('src');
            staleVideo.load();
          }
          staleVideo.remove();
        } catch (e) {
          // Ignore
        }
      }

      // Clean up any V1 player remnants that Chrome iOS might have kept alive
      // This ensures the hardware video decoder is released before we create a new one
      const v1Remnants = document.querySelectorAll(
        '#videoPlayerV1Id, #videoPlayerV1Id_html5_api, [id*="videoPlayerV1Id"]'
      );
      v1Remnants.forEach((el) => {
        try {
          if (el.tagName === 'VIDEO') {
            el.muted = true;
            el.pause();
            el.removeAttribute('src');
            el.load(); // Forces decoder release
          }
          el.remove();
        } catch (e) {
          // Ignore cleanup errors
        }
      });

      this.sourceVideo = document.createElement('video');
      this.sourceVideo.id = this.canvasPlayerId;
      // Use off-screen positioning instead of display:none
      // Chrome iOS does not decode video frames for display:none elements,
      // causing the canvas render loop to draw stale/frozen frames
      this.sourceVideo.style.position = 'absolute';
      this.sourceVideo.style.width = '1px';
      this.sourceVideo.style.height = '1px';
      this.sourceVideo.style.top = '0';
      this.sourceVideo.style.left = '0';
      this.sourceVideo.style.opacity = '0';
      this.sourceVideo.style.pointerEvents = 'none';
      this.sourceVideo.style.zIndex = '-1';
      this.sourceVideo.muted = true; // Must be true for iOS autoplay
      this.sourceVideo.autoplay = true;
      this.sourceVideo.playsInline = true;
      this.sourceVideo.setAttribute('webkit-playsinline', 'true');
      this.sourceVideo.setAttribute('playsinline', '');

      // Apply PiP disable attribute
      if (this.antiPipLayer) {
        this.antiPipLayer.protectVideoElement(this.sourceVideo);
      }

      // Append to body (off-screen, invisible but still decoded by Chrome iOS)
      document.body.appendChild(this.sourceVideo);

      // iOS Safari supports HLS natively - just set the src
      this.sourceVideo.src = normalizedUrl;

      this.sourceVideo.addEventListener('loadedmetadata', () => {
        this.updateCanvasSize();
      });

      // Apply persisted volume only AFTER play succeeds (browser requires muted for autoplay)
      this.sourceVideo.addEventListener(
        'playing',
        () => {
          this.updateVolume();
        },
        { once: true }
      );

      this.sourceVideo.addEventListener('error', (e) => {
        // Retry on any error
        this.scheduleRetry();
      });

      setTimeout(() => {
        if (!this.sourceVideo) return;
        const playPromise = this.sourceVideo.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => {
            console.error('iOS native play failed:', err);
          });
        }
      }, 500);
    },

    // Android displays the canvas directly; VePlayer only supplies decoded frames/audio.
    startPlayAndroid(normalizedUrl) {
      this.startPlayVePlayer(normalizedUrl, false);
    },

    startPlayCanvas(normalizedUrl) {
      this.startPlayVePlayer(normalizedUrl, true);
    },

    // Cleanup old canvas stream before setting up new one
    cleanupCanvasStream() {
      if (this.canvasStream) {
        try {
          // Stop all tracks to release resources
          this.canvasStream.getTracks().forEach((track) => {
            track.stop();
          });
        } catch (e) {
          console.warn('Error stopping canvas stream tracks:', e);
        }
        this.canvasStream = null;
      }

      // Clear output video srcObject
      if (this.outputVideo && this.outputVideo.srcObject) {
        this.outputVideo.srcObject = null;
      }
    },

    // Setup canvas stream for output video
    // Performance: FPS synced with render loop to avoid wasted frames
    setupCanvasStream() {
      if (!this.canvas || !this.outputVideo) return;

      // Clean up old stream first to prevent memory leaks
      this.cleanupCanvasStream();

      try {
        // Capture canvas stream at same FPS as render loop (30fps)
        // This ensures every rendered frame is captured, no wasted cycles
        this.canvasStream = this.canvas.captureStream(this.targetFPS);

        // Get audio from source video if available
        if (this.sourceVideo && this.sourceVideo.captureStream) {
          const audioStream = this.sourceVideo.captureStream();
          const audioTracks = audioStream.getAudioTracks();
          if (audioTracks.length > 0) {
            this.canvasStream.addTrack(audioTracks[0]);
          }
        }

        // Set the stream to output video
        this.outputVideo.srcObject = this.canvasStream;

        // Start both videos - apply persisted volume only after play succeeds (autoplay policy)
        this.sourceVideo
          .play()
          .then(() => {
            this.updateVolume();
          })
          .catch(() => {});
        this.outputVideo.play();
      } catch (error) {
        console.error('Error setting up canvas stream:', error);
      }
    },

    // Update canvas size to match video
    updateCanvasSize() {
      if (this.sourceVideo && this.canvas && this.sourceVideo.videoWidth > 0) {
        this.canvas.width = this.sourceVideo.videoWidth;
        this.canvas.height = this.sourceVideo.videoHeight;

        // Keep canvas display size responsive
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';

        // Invalidate robust watermark cache when canvas size changes
        if (this.robustWatermark) {
          this.robustWatermark.invalidateCache();
        }
      }
    },

    getExponentialJitter(meanSeconds) {
      const random = Math.random();
      const clampedRandom = Math.min(random, 0.9999);
      const jitter = -meanSeconds * Math.log(1 - clampedRandom);

      return isFinite(jitter) ? Math.min(jitter, 300) : meanSeconds;
    },

    // Start watermark frequency control
    startWatermarkFrequency() {
      // Safety check for empty streamWatermarkData
      if (!this.streamWatermarkData || typeof this.streamWatermarkData.watermarkFrequency === 'undefined') {
        this.watermarkVisible = false;
        return;
      }

      const frequency = this.streamWatermarkData.watermarkFrequency || 0;
      const config = this.frequencyConfig[frequency];

      if (!config) {
        this.watermarkVisible = false;
        return;
      }

      // Clear any existing timer
      if (this.watermarkTimer) {
        clearTimeout(this.watermarkTimer);
        this.watermarkTimer = null;
      }

      // Handle special modes
      if (config.mode === 'off') {
        this.watermarkVisible = false;
        return;
      }

      if (config.mode === 'always_on') {
        this.watermarkVisible = true;
        return;
      }

      // Start with visible state and randomize position for static watermarks
      this.watermarkVisible = true;
      this.randomizeStaticWatermarkPosition();
      this.scheduleWatermarkToggle();
    },

    // Schedule next watermark visibility toggle
    scheduleWatermarkToggle() {
      const frequency = (this.streamWatermarkData && this.streamWatermarkData.watermarkFrequency) || 0;
      const config = this.frequencyConfig[frequency];

      if (!config || config.mode === 'off' || config.mode === 'always_on') {
        return;
      }

      let delay;

      if (this.watermarkVisible) {
        // Currently visible, schedule to hide
        delay = this.getExponentialJitter(config.mean_on_s) * 1000;
      } else {
        // Currently hidden, schedule to show
        delay = this.getExponentialJitter(config.mean_off_s) * 1000;
      }

      // Add safety check for delay value
      // Maximum setTimeout value in JavaScript is 2147483647 (32-bit signed integer max)
      if (!isFinite(delay) || delay < 0 || delay > 2147483647) {
        console.warn('Invalid delay calculated, using default 5000ms:', delay);
        delay = 5000; // Default to 5 seconds
      }

      this.watermarkTimer = setTimeout(() => {
        const wasVisible = this.watermarkVisible;
        this.watermarkVisible = !this.watermarkVisible;

        // Update watermark visibility based on mode
        if (this.useDirectMode) {
          // CSS watermark (iOS/direct mode)
          this.updateCSSWatermarkVisibility();
        } else {
          // Canvas watermark (desktop mode)
          // If becoming visible and using static movement, randomize position
          if (this.watermarkVisible && !wasVisible) {
            this.randomizeStaticWatermarkPosition();
          }
        }

        this.scheduleWatermarkToggle();
      }, delay);
    },

    // Stop watermark frequency control
    stopWatermarkFrequency() {
      if (this.watermarkTimer) {
        clearTimeout(this.watermarkTimer);
        this.watermarkTimer = null;
      }
    },

    // Randomize position for static watermarks when they become visible
    randomizeStaticWatermarkPosition() {
      if (!this.canvas) return;

      this.overlays.forEach((overlay) => {
        if ((overlay.type === 'text' || overlay.type === 'image') && overlay.movementType === 0) {
          // Generate new random position for static watermarks
          overlay.x = Math.random() * this.canvas.width;
          overlay.y = Math.random() * this.canvas.height;
        }
      });
    },

    // Start corner-to-corner transition (for movement type 3)
    startCornerTransition() {
      // Initialize at top-left path, start position
      this.currentCorner = 'top-left';
      this.cornerAnimationPhase = 'start';

      // Set initial position for overlays
      this.overlays.forEach((overlay) => {
        if (overlay.movementType === 3) {
          // Path 1 start: left edge, 25% from top
          overlay.x = this.canvas.width * 0.0;
          overlay.y = this.canvas.height * 0.25;
          overlay.targetX = this.canvas.width * 0.15;
          overlay.targetY = this.canvas.height * 0.0;
          overlay.opacity = 0; // Start invisible, will fade in
        }
      });
    },

    // Main render loop - draws video + overlays to canvas
    startRenderLoop() {
      // Cancel any existing animation frame to prevent multiple loops
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }

      let frameCount = 0;
      this.lastFrameTime = performance.now();

      // 30fps throttling for consistent performance across all devices
      const frameInterval = this.frameInterval; // 1000/30 = ~33.33ms

      const render = (currentTime) => {
        // Calculate elapsed time since last frame
        const elapsed = currentTime - this.lastFrameTime;

        // Frame throttling - skip frame if too soon (reduces CPU/GPU load)
        if (elapsed < frameInterval) {
          this.animationId = requestAnimationFrame(render);
          return;
        }

        // Simple frame time update (no complex overshoot calculation)
        this.lastFrameTime = currentTime;

        // Calculate delta time in seconds for time-based animations
        const deltaTime = Math.min(elapsed / 1000, 0.1); // Cap at 100ms to prevent large jumps

        if (this.canvas && this.ctx && this.sourceVideo) {
          // Check if video has data
          if (this.sourceVideo.readyState >= this.sourceVideo.HAVE_CURRENT_DATA && this.sourceVideo.videoWidth > 0) {
            try {
              // Draw video frame to canvas (no clearRect - drawImage overwrites entire canvas)
              this.ctx.drawImage(this.sourceVideo, 0, 0, this.canvas.width, this.canvas.height);

              // Draw overlays (watermarks) with delta time for smooth animations
              this.drawOverlays(deltaTime);

              // Draw robust anti-piracy watermark
              this.drawRobustWatermark();

              frameCount = (frameCount + 1) % 1000;
            } catch (err) {
              if (frameCount === 0) {
                console.error('[tc-video] Error drawing video to canvas:', err);
              }
            }
          } else {
            // Video not ready, fill with black (only when no video data)
            this.ctx.fillStyle = '#0d1a24';
            this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
            frameCount = (frameCount + 1) % 1000;
          }
        }

        this.animationId = requestAnimationFrame(render);
      };

      this.animationId = requestAnimationFrame(render);
    },

    // Draw robust anti-piracy watermark (multiple faint moving instances)
    drawRobustWatermark() {
      // Only render robust watermark for types 3 and 4
      const watermarkType = (this.streamWatermarkData && this.streamWatermarkData.watermarkType) || 1;
      if (watermarkType !== 3 && watermarkType !== 4) {
        return;
      }

      // Check if watermark data exists and is not empty
      const watermarkText = this.streamWatermarkData.watermark;
      if (!watermarkText || String(watermarkText).trim() === '') {
        return;
      }

      if (!this.robustWatermark || !this.robustWatermarkEnabled) {
        return;
      }

      if (!this.canvas || !this.ctx) return;

      // Update watermark positions (smooth Perlin noise movement)
      this.robustWatermark.update();

      // Render all watermark instances
      this.robustWatermark.render(this.ctx, this.canvas.width, this.canvas.height);
    },

    // Draw overlays with time-delta for consistent animation speed
    drawOverlays(deltaTime = 0.033) {
      // Skip if no overlays to draw
      if (!this.overlays || this.overlays.length === 0) return;

      // Enable image smoothing for watermarks (set once, not per-overlay)
      this.ctx.imageSmoothingEnabled = true;

      this.overlays.forEach((overlay) => {
        // Skip rendering text/image watermarks if not visible (but still update position)
        if ((overlay.type === 'text' || overlay.type === 'image') && !this.watermarkVisible) {
          this.updateOverlayPosition(overlay, deltaTime);
          return;
        }

        this.ctx.save();

        // Apply opacity
        this.ctx.globalAlpha = overlay.opacity || 1;

        // Move to position
        this.ctx.translate(overlay.x, overlay.y);

        // Rotate if needed
        if (overlay.rotation) {
          this.ctx.rotate((overlay.rotation * Math.PI) / 180);
        }

        // Draw based on type
        if (overlay.type === 'logo' && overlay.image) {
          this.ctx.drawImage(overlay.image, -overlay.width / 2, -overlay.height / 2, overlay.width, overlay.height);
        } else if (overlay.type === 'image' && overlay.image) {
          // Image watermark (Type 2)
          this.ctx.drawImage(overlay.image, -overlay.width / 2, -overlay.height / 2, overlay.width, overlay.height);
        } else if (overlay.type === 'text') {
          // Text watermark (Type 1 and Type 5)
          this.ctx.font = overlay.font || '24px Arial';
          this.ctx.fillStyle = overlay.color || '#FFFFFF';
          this.ctx.textAlign = 'center';
          this.ctx.textBaseline = 'middle';

          // Add text shadow for better visibility (only for Type 1, skip for Type 5 which has custom opacity)
          // Type 5 watermarks have opacity property set, Type 1 does not
          if (overlay.opacity === undefined) {
            this.ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            this.ctx.shadowBlur = 4;
            this.ctx.shadowOffsetX = 2;
            this.ctx.shadowOffsetY = 2;
          } else {
            // Type 5: No shadow for subtle watermark
            this.ctx.shadowColor = 'transparent';
            this.ctx.shadowBlur = 0;
            this.ctx.shadowOffsetX = 0;
            this.ctx.shadowOffsetY = 0;
          }

          this.ctx.fillText(overlay.text, 0, 0);
        }

        this.ctx.restore();

        // Update overlay position for animation with time-delta
        this.updateOverlayPosition(overlay, deltaTime);
      });
    },

    // Update overlay position based on movement type
    // Uses time-delta for frame-rate independent animation (consistent speed at any FPS)
    updateOverlayPosition(overlay, deltaTime = 0.033) {
      // Handle different movement types
      if (overlay.animate && overlay.movementType !== undefined) {
        switch (overlay.movementType) {
          case 0: // Static - no movement
            // Position stays fixed at initial random position
            break;

          case 1: // Random smooth movement
            // Initialize target position if not set
            if (!overlay.targetX || !overlay.targetY) {
              overlay.targetX = Math.random() * this.canvas.width;
              overlay.targetY = Math.random() * this.canvas.height;
            }

            // Calculate distance to target
            const dx = overlay.targetX - overlay.x;
            const dy = overlay.targetY - overlay.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 5) {
              overlay.targetX = Math.random() * this.canvas.width;
              overlay.targetY = Math.random() * this.canvas.height;
            } else {
              // Time-based speed: consistent animation regardless of frame rate
              const speed = 0.6 * deltaTime;
              overlay.x += dx * speed;
              overlay.y += dy * speed;
            }
            break;

          case 2: // Marquee - horizontal scrolling
            // Convert to pixels per second for time-based animation
            const marqueePixelsPerSecond = overlay.marqueeSpeed ? overlay.marqueeSpeed * 30 : 60;

            // Move from right to left (time-based)
            overlay.x -= marqueePixelsPerSecond * deltaTime;

            // Reset to right side when it goes off screen
            if (overlay.x < -50) {
              overlay.x = this.canvas.width + 50;
            }
            break;

          case 3: // Diagonal movement from edges
            // Set target based on current corner and phase (fallback if not already set)
            if (!overlay.targetX || !overlay.targetY) {
              if (this.currentCorner === 'top-left') {
                // Path 1: left edge to top edge
                overlay.targetX = this.canvas.width * 0.15;
                overlay.targetY = this.canvas.height * 0.0;
              } else {
                // Path 2: bottom edge to right edge
                overlay.targetX = this.canvas.width * 0.95;
                overlay.targetY = this.canvas.height * 0.75;
              }
            }

            // Smooth interpolation towards target
            const dx3 = overlay.targetX - overlay.x;
            const dy3 = overlay.targetY - overlay.y;
            const distance3 = Math.sqrt(dx3 * dx3 + dy3 * dy3);

            if (distance3 > 5) {
              // Time-based speed for smooth transition
              const speed = 0.3 * deltaTime;
              overlay.x += dx3 * speed;
              overlay.y += dy3 * speed;

              // Fade in while moving (time-based)
              overlay.opacity = Math.min(1, overlay.opacity + 0.9 * deltaTime);
            } else {
              // Reached target - immediately fade out and switch to next path
              overlay.opacity = 0;

              // Switch to next path immediately
              if (this.currentCorner === 'top-left') {
                // Switch to Path 2: bottom to right
                this.currentCorner = 'bottom-right';
                overlay.x = this.canvas.width * 0.75;
                overlay.y = this.canvas.height * 1.0;
                overlay.targetX = this.canvas.width * 0.95;
                overlay.targetY = this.canvas.height * 0.75;
              } else {
                // Switch to Path 1: left to top
                this.currentCorner = 'top-left';
                overlay.x = this.canvas.width * 0.0;
                overlay.y = this.canvas.height * 0.25;
                overlay.targetX = this.canvas.width * 0.15;
                overlay.targetY = this.canvas.height * 0.0;
              }
            }
            break;
        }
      } else if (overlay.animate) {
        // Fallback for overlays without movementType (legacy)
        if (!overlay.targetX || !overlay.targetY) {
          overlay.targetX = Math.random() * this.canvas.width;
          overlay.targetY = Math.random() * this.canvas.height;
        }

        const dx = overlay.targetX - overlay.x;
        const dy = overlay.targetY - overlay.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 5) {
          overlay.targetX = Math.random() * this.canvas.width;
          overlay.targetY = Math.random() * this.canvas.height;
        } else {
          // Time-based speed
          const speed = 0.6 * deltaTime;
          overlay.x += dx * speed;
          overlay.y += dy * speed;
        }
      }
    },

    _addOvl() {
      if (!this.ctx || !this.canvas) {
        setTimeout(() => this._addOvl(), 100);
        return;
      }

      const img = new Image();
      img.onload = () => {
        const ovl = {
          type: 'logo',
          image: img,
          x: Math.random() * this.canvas.width,
          y: Math.random() * this.canvas.height,
          width: 150,
          height: 50,
          opacity: 0.8,
          rotation: 0,
          animate: true,
          persistent: true,
          timestamp: new Date().toISOString(),
        };

        this.overlays.push(ovl);
      };
      img.src = require('@/assets/images/logos/default.webp');
    },

    convertStreamErrorMessage(errorMessage) {
      const match = errorMessage.match(/([A-Z]{3})\s*(\d+)/);
      if (match) {
        const currentCurrency = match[1];
        const currentBalance = match[2];
        return this.$t('You need at least {CURRENCY} {AMOUNT} in your wallet to watch this stream.', {
          CURRENCY: currentCurrency,
          AMOUNT: currentBalance,
        });
      }
      return errorMessage;
    },
    // listen event
    getActiveResolution() {
      return this.resolutions[this.activeResolutionIndex];
    },

    // Add text overlay (supports both text and image watermarks)
    addText() {
      if (!this.ctx || !this.canvas) {
        setTimeout(() => this.addText(), 100);
        return;
      }

      const watermarkType = this.streamWatermarkData.watermarkType || 1;
      const movementType = this.streamWatermarkData.watermarkMovement || 0;

      // Disable robust watermark for types 1, 2, and 5 (text/image only watermarks)
      if (watermarkType === 1 || watermarkType === 2 || watermarkType === 5) {
        this.robustWatermarkEnabled = false;
        if (this.robustWatermark) {
          this.robustWatermark.setEnabled(false);
        }
      }

      // Font size from BE config, only for text watermarks (types 1, 4, 5)
      const watermarkFontSize = this.streamWatermarkData.watermarkFontSize;
      const fontSizeFromBE =
        watermarkFontSize != null && !Number.isNaN(Number(watermarkFontSize)) && Number(watermarkFontSize) > 0
          ? Math.round(Number(watermarkFontSize))
          : null;

      // Type 1: Text watermark
      if (watermarkType === 1) {
        const displayText = this.streamWatermarkData.watermark;

        let fontSize;
        if (fontSizeFromBE !== null) {
          fontSize = fontSizeFromBE;
        } else {
          // Fallback: detect device type for responsive font size
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
          const isAndroid = /Android/i.test(navigator.userAgent);

          const canvasShortSide = Math.min(this.canvas.width, this.canvas.height);

          let fontSizePercentage, maxFontSize, minFontSize;
          if (isAndroid) {
            fontSizePercentage = 0.08;
            maxFontSize = 42;
            minFontSize = 32;
          } else if (isIOS) {
            fontSizePercentage = 0.04;
            maxFontSize = 24;
            minFontSize = 18;
          } else {
            fontSizePercentage = 0.04;
            maxFontSize = 30;
            minFontSize = 24;
          }

          fontSize = canvasShortSide * fontSizePercentage;
          fontSize = Math.min(fontSize, maxFontSize);
          fontSize = Math.max(fontSize, minFontSize);
        }

        this.overlays.push({
          type: 'text',
          text: displayText,
          x: Math.random() * this.canvas.width,
          y: Math.random() * this.canvas.height,
          font: `${Math.round(fontSize)}px Arial`,
          color: '#FF0000',
          animate: movementType !== 0, // Only animate if not static
          movementType: movementType, // 0: static, 1: random, 2: marquee, 3: corner-to-corner
        });
      }
      // Type 2: Image watermark
      else if (watermarkType === 2) {
        const imageUrl = this.streamWatermarkData.watermarkImageUrl;
        if (!imageUrl) {
          console.warn('Watermark image URL not provided');
          return;
        }

        // Load image
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Handle CORS if needed
        img.onload = () => {
          // Calculate scaled dimensions while maintaining aspect ratio
          const naturalWidth = img.naturalWidth || img.width;
          const naturalHeight = img.naturalHeight || img.height;

          // Detect mobile vs desktop for better sizing
          const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          );
          const isSmallScreen = window.innerWidth <= 768;
          const isMobile = isMobileDevice || isSmallScreen;

          let scaledWidth = naturalWidth;
          let scaledHeight = naturalHeight;

          const maxSize = isMobile ? 80 : 120;
          const minSize = isMobile ? 30 : 40;

          // Scale down if image is larger than max size
          const maxDimension = Math.max(scaledWidth, scaledHeight);
          if (maxDimension > maxSize) {
            const scale = maxSize / maxDimension;
            scaledWidth *= scale;
            scaledHeight *= scale;
          }

          // Scale up if image is smaller than min size
          const minDimension = Math.min(scaledWidth, scaledHeight);
          if (minDimension < minSize) {
            const scale = minSize / minDimension;
            scaledWidth *= scale;
            scaledHeight *= scale;
          }

          // === HIGH QUALITY RENDERING ===
          const renderScale = 3;
          const offscreenCanvas = document.createElement('canvas');
          offscreenCanvas.width = scaledWidth * renderScale;
          offscreenCanvas.height = scaledHeight * renderScale;

          const offscreenCtx = offscreenCanvas.getContext('2d');

          // Enable high-quality rendering on off-screen canvas
          offscreenCtx.imageSmoothingEnabled = true;
          offscreenCtx.imageSmoothingQuality = 'high';

          // Draw the original image to off-screen canvas at higher resolution
          offscreenCtx.drawImage(img, 0, 0, offscreenCanvas.width, offscreenCanvas.height);

          // Calculate initial position based on movement type
          let initialX, initialY, initialOpacity;
          if (movementType === 3) {
            // Diagonal movement: start at left edge, 25% from top
            initialX = this.canvas.width * 0.0;
            initialY = this.canvas.height * 0.25;
            initialOpacity = 0; // Start invisible, will fade in
          } else {
            // Other types: random position
            initialX = Math.random() * this.canvas.width;
            initialY = Math.random() * this.canvas.height;
            initialOpacity = 1;
          }

          // Store the pre-rendered canvas instead of the original image
          this.overlays.push({
            type: 'image',
            image: offscreenCanvas, // Use pre-rendered canvas instead of img
            x: initialX,
            y: initialY,
            width: scaledWidth,
            height: scaledHeight,
            opacity: initialOpacity,
            animate: movementType !== 0,
            movementType: movementType, // 0: static, 1: random, 2: marquee, 3: corner-to-corner
          });

          // Start corner transition timer for movement type 3
          if (movementType === 3) {
            this.startCornerTransition();
          }
        };
        img.onerror = () => {
          console.error('Failed to load watermark image:', imageUrl);
        };

        const isProduction = isProdMode() || process.env.NODE_ENV === 'production';

        // If it's a relative path, prepend with base URL
        const baseUrl = getBaseUrl();
        const separator = imageUrl.startsWith('/') ? '' : '/';
        img.src = `https://${isProduction ? 'api' : 'gc-api-dev'}.${baseUrl}${separator}${imageUrl}`;
      }
      // Type 3: Only robust/opacity mask watermark (no text overlay)
      else if (watermarkType === 3) {
        const username = this.streamWatermarkData.watermark || 'USER';

        // Initialize robust watermark with username from streamWatermarkData
        if (!this.robustWatermark) {
          this.robustWatermark = createRobustWatermark(username, 'moderate');
          if (this.canvas) {
            this.robustWatermark.initialize(this.canvas);
          }
        } else {
          // Update existing robust watermark with new username
          this.robustWatermark.updateConfig({ text: username });
          // Re-initialize if canvas exists (in case canvas was resized or recreated)
          if (this.canvas) {
            this.robustWatermark.initialize(this.canvas);
          }
        }

        // Enable robust watermark
        this.robustWatermarkEnabled = true;
        if (this.robustWatermark) {
          this.robustWatermark.setEnabled(true);
        }
      }
      // Type 4: Both text watermark (type 1) AND robust/opacity mask watermark
      else if (watermarkType === 4) {
        const displayText = this.streamWatermarkData.watermark;
        const username = this.streamWatermarkData.watermark || 'USER';

        // Add text watermark (same logic as type 1)
        let fontSize;
        if (fontSizeFromBE !== null) {
          fontSize = fontSizeFromBE;
        } else {
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
          const isAndroid = /Android/i.test(navigator.userAgent);

          const canvasShortSide = Math.min(this.canvas.width, this.canvas.height);

          let fontSizePercentage, maxFontSize, minFontSize;
          if (isAndroid) {
            fontSizePercentage = 0.08;
            maxFontSize = 42;
            minFontSize = 32;
          } else if (isIOS) {
            fontSizePercentage = 0.04;
            maxFontSize = 24;
            minFontSize = 18;
          } else {
            fontSizePercentage = 0.04;
            maxFontSize = 30;
            minFontSize = 24;
          }

          fontSize = canvasShortSide * fontSizePercentage;
          fontSize = Math.min(fontSize, maxFontSize);
          fontSize = Math.max(fontSize, minFontSize);
        }

        this.overlays.push({
          type: 'text',
          text: displayText,
          x: Math.random() * this.canvas.width,
          y: Math.random() * this.canvas.height,
          font: `${Math.round(fontSize)}px Arial`,
          color: '#FF0000',
          animate: movementType !== 0, // Only animate if not static
          movementType: movementType, // 0: static, 1: random, 2: marquee, 3: corner-to-corner
        });

        // Initialize robust watermark with username from streamWatermarkData
        if (!this.robustWatermark) {
          this.robustWatermark = createRobustWatermark(username, 'moderate');
          if (this.canvas) {
            this.robustWatermark.initialize(this.canvas);
          }
        } else {
          // Update existing robust watermark with new username
          this.robustWatermark.updateConfig({ text: username });
          // Re-initialize if canvas exists (in case canvas was resized or recreated)
          if (this.canvas) {
            this.robustWatermark.initialize(this.canvas);
          }
        }

        // Enable robust watermark
        this.robustWatermarkEnabled = true;
        if (this.robustWatermark) {
          this.robustWatermark.setEnabled(true);
        }
      }
      // Type 5: Text watermark with configurable color and opacity (subtle, non-disruptive)
      else if (watermarkType === 5) {
        const displayText = this.streamWatermarkData.watermark;

        // Get color and opacity from backend, with sensible defaults for subtle watermark
        // Default: white color with 30% opacity for minimal disruption
        const watermarkColor = this.streamWatermarkData.watermarkColor || '#FFFFFF';
        const watermarkOpacity = this.streamWatermarkData.watermarkOpacity || 0.4;

        let fontSize;
        if (fontSizeFromBE !== null) {
          fontSize = fontSizeFromBE;
        } else {
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
          const isAndroid = /Android/i.test(navigator.userAgent);

          const canvasShortSide = Math.min(this.canvas.width, this.canvas.height);

          let fontSizePercentage, maxFontSize, minFontSize;
          if (isAndroid) {
            fontSizePercentage = 0.08;
            maxFontSize = 42;
            minFontSize = 32;
          } else if (isIOS) {
            fontSizePercentage = 0.04;
            maxFontSize = 24;
            minFontSize = 18;
          } else {
            fontSizePercentage = 0.04;
            maxFontSize = 30;
            minFontSize = 24;
          }

          fontSize = canvasShortSide * fontSizePercentage;
          fontSize = Math.min(fontSize, maxFontSize);
          fontSize = Math.max(fontSize, minFontSize);
        }

        this.overlays.push({
          type: 'text',
          text: displayText,
          x: Math.random() * this.canvas.width,
          y: Math.random() * this.canvas.height,
          font: `${Math.round(fontSize)}px Arial`,
          color: watermarkColor,
          opacity: watermarkOpacity, // Configurable opacity for subtle display
          animate: movementType !== 0, // Only animate if not static
          movementType: movementType, // 0: static, 1: random, 2: marquee, 3: corner-to-corner
        });
      }
    },

    // Add CSS-based watermark for direct mode (iOS)
    addCSSWatermark() {
      if (!this.streamWatermarkData || !this.streamWatermarkData.watermark) {
        return;
      }

      const container = this.$refs.videoPlayerContainer;
      if (!container) {
        setTimeout(() => this.addCSSWatermark(), 100);
        return;
      }

      // Remove existing watermark if present
      if (this.watermarkElement) {
        this.watermarkElement.remove();
        this.watermarkElement = null;
      }

      const displayText = this.streamWatermarkData.watermark;
      const movementType = this.streamWatermarkData.watermarkMovement || 0;

      const fontSizeFromBE =
        this.streamWatermarkData.watermarkFontSize != null &&
        !Number.isNaN(Number(this.streamWatermarkData.watermarkFontSize)) &&
        Number(this.streamWatermarkData.watermarkFontSize) > 0
          ? Math.round(Number(this.streamWatermarkData.watermarkFontSize))
          : 30;

      // Create watermark element
      this.watermarkElement = document.createElement('div');
      this.watermarkElement.className = 'css-watermark';
      this.watermarkElement.textContent = displayText;
      this.watermarkElement.style.cssText = `
        position: absolute;
        color: #FF0000;
        font-size: ${fontSizeFromBE}px;
        font-family: Arial, sans-serif;
        font-weight: bold;
        opacity: 0.8;
        z-index: 1001;
        pointer-events: none;
        white-space: nowrap;
        text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
        transition: opacity 0.3s ease;
      `;

      // Set initial position
      this.updateCSSWatermarkPosition();

      // Add to container
      container.appendChild(this.watermarkElement);

      // Start animation based on movement type
      if (movementType === 1) {
        // Random smooth movement
        this.startCSSWatermarkAnimation();
      } else if (movementType === 2) {
        // Marquee movement
        this.startCSSMarqueeAnimation();
      }
    },

    // Update CSS watermark position
    updateCSSWatermarkPosition() {
      if (!this.watermarkElement) return;

      const container = this.$refs.videoPlayerContainer;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const watermarkWidth = this.watermarkElement.offsetWidth || 200;
      const watermarkHeight = this.watermarkElement.offsetHeight || 40;

      // Random position within bounds
      const maxX = Math.max(0, containerWidth - watermarkWidth);
      const maxY = Math.max(0, containerHeight - watermarkHeight);

      const x = Math.random() * maxX;
      const y = Math.random() * maxY;

      this.watermarkElement.style.left = `${x}px`;
      this.watermarkElement.style.top = `${y}px`;
    },

    // Start CSS watermark animation (random movement)
    startCSSWatermarkAnimation() {
      if (!this.watermarkElement) return;

      const animate = () => {
        if (!this.watermarkElement || !this.useDirectMode) return;

        // Only reposition when visible
        if (this.watermarkVisible) {
          this.watermarkElement.style.transition = 'left 3s ease-in-out, top 3s ease-in-out';
          this.updateCSSWatermarkPosition();
        }

        // Continue animation
        setTimeout(animate, 3000);
      };

      animate();
    },

    // Start CSS marquee animation
    startCSSMarqueeAnimation() {
      if (!this.watermarkElement) return;

      const container = this.$refs.videoPlayerContainer;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Start from right side
      this.watermarkElement.style.left = `${containerWidth}px`;
      this.watermarkElement.style.top = `${containerHeight / 2}px`;

      const animateMarquee = () => {
        if (!this.watermarkElement || !this.useDirectMode) return;

        const currentLeft = parseFloat(this.watermarkElement.style.left) || containerWidth;
        const watermarkWidth = this.watermarkElement.offsetWidth || 200;

        if (currentLeft < -watermarkWidth) {
          this.watermarkElement.style.left = `${containerWidth}px`;
        } else {
          this.watermarkElement.style.left = `${currentLeft - 2}px`;
        }

        requestAnimationFrame(animateMarquee);
      };

      animateMarquee();
    },

    // Update CSS watermark visibility
    updateCSSWatermarkVisibility() {
      if (!this.watermarkElement) return;

      if (this.watermarkVisible) {
        this.watermarkElement.style.opacity = '0.8';

        // Reposition static watermarks when they become visible
        const movementType = this.streamWatermarkData.watermarkMovement || 0;
        if (movementType === 0) {
          this.updateCSSWatermarkPosition();
        }
      } else {
        this.watermarkElement.style.opacity = '0';
      }
    },

    // Clear all overlays
    clearOverlays() {
      this.overlays = [];
    },

    _initSysIntegrity() {
      if (!this._sysEn || this._intMgr) return;

      this._intMgr = new _SysIntegrity({
        layer1: {
          enabled: this._l1En,
          opacity: 0.8,
          animationSpeed: 1.0,
          pattern: 'smooth',
          watermarkImage: '',
          showUserId: true,
          showTimestamp: true,
        },
        protection: {
          enabled: true,
          autoReinstate: true,
          strictMode: true,
          checkInterval: 1000,
        },
        antiInspect: {
          enabled: true,
        },
        user: {
          id: this.member.id || 'unknown',
          displayName: this.memberName,
          sessionId: `session_${Date.now()}`,
        },
      });

      const _elems = [this.canvas, this.outputVideo].filter(Boolean);

      if (_elems.length > 0) {
        this._intMgr.initialize(
          _elems,
          (ev) => this._hdlSysEv(ev),
          (cmd) => this._hdlBkCmd(cmd)
        );

        if (this.outputVideo) {
          this._intMgr.monitorPiPEvents(this.outputVideo);
        }
      }
    },

    _hdlSysEv(ev) {
      if (ev.severity === 'critical') {
        console.error('Critical event detected');
      }
    },

    _hdlBkCmd(cmd) {},

    _getSysStats() {
      if (this._intMgr) {
        return this._intMgr.getStatistics();
      }
      return null;
    },

    // Touch start handler - track initial touch position
    handleTouchStart(event) {
      this.touchStartY = event.touches[0].clientY;
    },

    // Touch move handler - prevent pull-to-refresh on iOS
    handleTouchMove(event) {
      if (!this.touchStartY) return;

      const touchY = event.touches[0].clientY;
      const deltaY = touchY - this.touchStartY;

      // If pulling down from top of video, prevent default to stop pull-to-refresh
      if (deltaY > 0 && window.scrollY === 0) {
        event.preventDefault();
      }
    },

    // Canvas click handler - shows/hides controls menu
    handleCanvasClick() {
      if (this.isPaused) {
        // If paused, do nothing - let the overlay handle it
        return;
      }

      if (this.showControls) {
        // If controls are showing, hide them (tap outside controls)
        this.hideControlsMenu();
      } else {
        // Show controls menu
        this.showControlsMenu();
      }
    },

    // Show controls menu and start auto-hide timer
    showControlsMenu() {
      this.showControls = true;
      this.resetControlsTimer();
    },

    // Hide controls menu
    hideControlsMenu() {
      this.showControls = false;
      this.clearControlsTimer();
    },

    // Reset the auto-hide timer
    resetControlsTimer() {
      this.clearControlsTimer();
      this.controlsTimer = setTimeout(() => {
        this.hideControlsMenu();
      }, this.controlsAutoHideDelay);
    },

    // Clear the auto-hide timer
    clearControlsTimer() {
      if (this.controlsTimer) {
        clearTimeout(this.controlsTimer);
        this.controlsTimer = null;
      }
    },

    // Handle click on controls area (prevent closing)
    handleControlsClick(event) {
      // Stop propagation and reset timer when clicking on controls
      event.stopPropagation();
      this.resetControlsTimer();
    },

    // Handle volume button click
    handleVolumeClick(event) {
      event.stopPropagation();
      this.toggleMute();
      this.resetControlsTimer();
    },

    // Handle pause button click
    handlePauseClick(event) {
      event.stopPropagation();
      this.handlePause();
      // Keep controls visible when paused (they will show play button)
    },

    // Handle play button click (when paused)
    handlePlayClick(event) {
      event.stopPropagation();
      this.handlePlay();
      // Start auto-hide timer after resuming
      this.resetControlsTimer();
    },

    // Toggle pause/play
    togglePause() {
      if (this.isPaused) {
        // Resume playing
        this.handlePlay();
      } else {
        // Pause
        this.handlePause();
      }
    },

    // Toggle mute/unmute
    toggleMute() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

      if (this.volume === 0) {
        // Currently muted, restore previous volume
        // On iOS, always unmute to max volume (100) since intermediate values aren't supported
        if (isIOS) {
          this.volume = 100;
          // Ensure previousVolume is set for future use
          if (this.previousVolume === 0) {
            this.previousVolume = 100;
          }
        } else {
          this.volume = this.previousVolume || 50; // Fallback to 50 if previousVolume is 0
        }
      } else {
        // Currently unmuted, mute it
        this.previousVolume = this.volume;
        this.volume = 0;
      }
      this.updateVolume();
    },

    // Update volume
    updateVolume() {
      const volumeLevel = this.volume / 100;

      // Update CSS variable for progress bar
      this.$nextTick(() => {
        const slider = this.$el.querySelector('.volume-slider');
        if (slider) {
          slider.style.setProperty('--volume-percent', this.volume);
        }
      });

      // Update video elements based on mode
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

      if (isIOS) {
        // iOS: Safari only allows programmatic volume control to 0 (mute) or 1 (unmute)
        // Intermediate values are not supported for security/UX reasons
        if (this.sourceVideo) {
          if (this.volume === 0) {
            // Mute
            this.sourceVideo.muted = true;
            this.sourceVideo.volume = 0;
          } else {
            // Unmute - set to max volume (1.0) since iOS doesn't support intermediate values
            this.sourceVideo.muted = false;
            this.sourceVideo.volume = 1.0;
            // Store the desired volume level for when user switches to desktop
            // but on iOS, it will always be max volume when unmuted
          }
        }
      } else {
        // VePlayer's underlying video is the audio source.
        if (this.sourceVideo) {
          this.sourceVideo.volume = volumeLevel;
          this.sourceVideo.muted = this.volume === 0;
        }
        if (this.outputVideo) {
          this.outputVideo.volume = volumeLevel;
          this.outputVideo.muted = this.volume === 0;
        }
      }

      // Persist canvas volume only (canvas syncs with canvas when switching back to canvas)
      try {
        window.localStorage.setItem('tcCanvasMuted', String(this.volume === 0));
        window.localStorage.setItem('tcCanvasVolume', (this.volume / 100).toFixed(2));
      } catch (e) {
        // Ignore (e.g. private mode)
      }
    },

    handlePause() {
      this.isPaused = true;
      this.pauseStartTime = Date.now();

      // Clear auto-hide timer when paused (controls stay visible with play button)
      this.clearControlsTimer();

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS) {
        // iOS: Only pause sourceVideo (canvas displays it)
        if (this.sourceVideo) {
          this.sourceVideo.pause();
        }
      } else {
        // VePlayer-backed canvas: pause its underlying video and output.
        if (this.sourceVideo) {
          this.sourceVideo.pause();
        }
        if (this.outputVideo) {
          this.outputVideo.pause();
        }
      }
    },

    handlePlay() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

      // Skip check if we just reinitialized
      if (this.skipNextPlayCheck) {
        this.skipNextPlayCheck = false;
        this.lastPlayTime = Date.now();
        this.pauseStartTime = null;
        this.pauseDuration = 0;
        this.isPaused = false;

        // Play based on platform
        if (isIOS) {
          // iOS: Only play sourceVideo (canvas displays it)
          if (this.sourceVideo) {
            this.sourceVideo.play().catch((err) => console.warn('iOS skip check play failed:', err));
          }
        } else {
          // VePlayer-backed canvas: play its underlying video and output.
          if (this.sourceVideo) {
            this.sourceVideo.play().catch((err) => console.warn('Skip check source play failed:', err));
          }
          if (this.outputVideo) {
            this.outputVideo.play().catch((err) => console.warn('Skip check output play failed:', err));
          }
        }
        return;
      }

      if (this.pauseStartTime) {
        const currentPauseDuration = Date.now() - this.pauseStartTime;
        this.pauseDuration += currentPauseDuration;
        if (currentPauseDuration > this.refreshThreshold || this.pauseDuration > this.refreshThreshold) {
          this.reinitializePlayer();
          return; // Exit early since we're reinitializing
        }
      }

      this.isPaused = false;
      this.lastPlayTime = Date.now();
      this.pauseStartTime = null;

      setTimeout(() => {
        if (!this.isPaused && this.pauseDuration > 0) {
          this.pauseDuration = 0;
        }
      }, 10000);

      // Play based on platform
      if (isIOS) {
        // iOS: Only play sourceVideo (canvas displays it)
        if (this.sourceVideo) {
          this.sourceVideo.play().catch((err) => console.warn('iOS play failed:', err));
        }
      } else {
        // VePlayer-backed canvas: play its underlying video and output.
        if (this.sourceVideo) {
          this.sourceVideo.play().catch((err) => console.warn('Canvas mode source play failed:', err));
        }
        if (this.outputVideo) {
          this.outputVideo.play().catch((err) => console.warn('Canvas mode output play failed:', err));
        }
      }
    },

    reinitializePlayer() {
      if (!this.streamUrl || this.isReinitializing) {
        return;
      }

      // Skip for WebRTC URLs
      if (this.isWebRTCStream) {
        return;
      }

      this.isReinitializing = true;
      this.skipNextPlayCheck = true;
      this.pauseStartTime = null;
      this.pauseDuration = 0;

      // Normalize URL for iOS devices
      console.log('[tc-video] Reinitializing - Original URL:', this.streamUrl);
      const normalizedUrl = normalizeStreamUrl(this.streamUrl);
      console.log('[tc-video] Reinitializing - Normalized URL:', normalizedUrl);

      // Add cache-busting parameter
      const timestamp = Date.now();
      const urlWithCache = normalizedUrl.includes('?')
        ? `${normalizedUrl}&_=${timestamp}`
        : `${normalizedUrl}?_=${timestamp}`;

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isAndroid = /Android/i.test(navigator.userAgent);

      if (isIOS && this.sourceVideo) {
        // iOS: Reinitialize the native HLS video element.

        let metadataLoaded = false;
        let playbackStarted = false;

        const onLoadedMetadata = () => {
          if (metadataLoaded || !this.sourceVideo) return;
          metadataLoaded = true;

          const playPromise = this.sourceVideo.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                this.isPaused = false;
              })
              .catch((err) => {
                this.isReinitializing = false;
                this.isPaused = false;
              });
          }
        };

        const onPlaying = () => {
          if (playbackStarted) return;
          playbackStarted = true;
          this.updateVolume(); // Apply persisted volume after play succeeds
          setTimeout(() => {
            this.isReinitializing = false;
          }, 500);
        };

        const onError = (e) => {
          this.isReinitializing = false;
          this.isPaused = false;
        };

        // Add event listeners
        this.sourceVideo.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
        this.sourceVideo.addEventListener('playing', onPlaying, { once: true });
        this.sourceVideo.addEventListener('error', onError, { once: true });

        // Mute before reload so autoplay succeeds, then apply volume in onPlaying
        this.sourceVideo.muted = true;
        // Update the src to trigger reload
        this.sourceVideo.src = urlWithCache;
        this.sourceVideo.load();

        // Fallback timeout
        setTimeout(() => {
          if (this.isReinitializing) {
            this.isReinitializing = false;
            this.isPaused = false;
            if (this.sourceVideo && this.sourceVideo.paused) {
              this.sourceVideo.play().catch((err) => console.warn('[tc-video] iOS timeout play failed:', err));
            }
          }
        }, 3000);
      } else {
        const useCanvasCapture = !isAndroid;
        if (this.usingTcPlayerFallback) {
          this.startPlayTcPlayer(urlWithCache, useCanvasCapture);
          this.isReinitializing = false;
          this.isPaused = false;
        } else {
          // Recreate VePlayer so protocol fallback and low-latency settings are reapplied.
          this.startPlayVePlayer(urlWithCache, useCanvasCapture).finally(() => {
            this.isReinitializing = false;
            this.isPaused = false;
          });
        }
      }
    },

    // Request a fresh stream URL from the server
    refreshStreamUrl() {
      if (this.arena && this.arena.id) {
        console.log('[tc-video] Requesting fresh stream URL for arena:', this.arena.id);
        this.getArenaStreamUrl(this.arena.id);
      }
    },

    // Error retry methods
    scheduleRetry() {
      if (this.retryTimer) {
        return;
      }

      // Skip retry for WebRTC URLs - they're not supported by this player
      if (this.isWebRTCStream) {
        return;
      }

      this.retryCount++;
      this.isRetrying = true; // Show error overlay

      // On third retry, immediately request a fresh stream URL (URL might be expired)
      if (this.retryCount === 3) {
        console.log('[tc-video] Stream error detected, requesting fresh stream URL...');
        this.refreshStreamUrl();
        // Continue with retry to check if new URL works
      }

      // If exceeded max retries, request fresh URL one more time and reset
      if (this.retryCount > this.maxRetries) {
        if (!this.usingTcPlayerFallback) {
          const isAndroid = /Android/i.test(navigator.userAgent);
          this.activateTcPlayerFallback(normalizeStreamUrl(this.streamUrl), !isAndroid);
          return;
        }
        this.refreshStreamUrl();
        this.retryCount = 0;
        // Keep isRetrying true - will be cleared when new stream URL arrives
        return;
      }

      // Use simple setTimeout - most performant timer
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null; // Clear reference immediately

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        if (isIOS && this.sourceVideo && this.streamUrl) {
          // iOS: Reload the native video source
          const normalizedUrl = normalizeStreamUrl(this.streamUrl);
          const timestamp = Date.now();
          const urlWithCache = normalizedUrl.includes('?')
            ? `${normalizedUrl}&_=${timestamp}`
            : `${normalizedUrl}?_=${timestamp}`;

          this.sourceVideo.src = urlWithCache;
          this.sourceVideo.load();

          // Continue retry cycle
          this.scheduleRetry();
        } else if (this.streamUrl) {
          const isAndroid = /Android/i.test(navigator.userAgent);
          if (this.usingTcPlayerFallback) {
            this.startPlayTcPlayer(normalizeStreamUrl(this.streamUrl), !isAndroid);
          } else {
            this.startPlayVePlayer(normalizeStreamUrl(this.streamUrl), !isAndroid);
          }
        } else {
          this.scheduleRetry();
        }
      }, 5000); // Fixed 5 second interval - predictable and efficient
    },

    stopRetry() {
      if (!this.retryTimer && this.retryCount === 0 && !this.isRetrying) {
        return; // Already stopped
      }

      // Clear retry timer
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
      // Reset retry count and hide overlay
      this.retryCount = 0;
      this.isRetrying = false;
    },

    // Handle browser tab/app visibility change
    handleVisibilityChange() {
      if (document.hidden) {
        // Page is now hidden (user switched away)
        this.pageHiddenTime = Date.now();
      } else {
        // Page is now visible (user returned)
        if (this.pageHiddenTime) {
          const hiddenDuration = Date.now() - this.pageHiddenTime;

          // If hidden for longer than 60 seconds, stream URL might be stale - get fresh one
          if (hiddenDuration > 60000 && this.arena && this.arena.id) {
            console.log(
              '[tc-video] Page was hidden for',
              Math.round(hiddenDuration / 1000),
              'seconds, requesting fresh stream URL'
            );
            this.stopRetry(); // Clear any existing retry state
            this.refreshStreamUrl();
          }
          // If hidden for longer time (tab switch, not pull-to-refresh), seek to live edge
          // Skip seek for short absences (1-5s) - pull-to-refresh causes buffer thrashing and lag
          else if (hiddenDuration > this.seekVisibilityThreshold && this.streamUrl && !this.isReinitializing) {
            // If currently retrying, get fresh URL instead of just seeking
            if (this.isRetrying) {
              console.log('[tc-video] Returning from hidden state while retrying, requesting fresh stream URL');
              this.refreshStreamUrl();
            } else {
              this.seekToLiveEdge();
            }
          }

          this.pageHiddenTime = null;
        }

        // Always ensure auto-play when page becomes visible on mobile (handles pull-to-refresh)
        this.ensureAutoPlay();
      }
    },

    // Ensure video is playing after visibility change / pull-to-refresh on mobile
    // On mobile with volume ON, browser blocks programmatic play - mute first, play, then restore volume
    ensureAutoPlay() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isMobile = isIOS || isAndroid;

      if (this.sourceVideo) {
        if (this.sourceVideo.paused && this.streamUrl) {
          if (isMobile) this.sourceVideo.muted = true;
          this.sourceVideo
            .play()
            .then(() => {
              this.isPaused = false;
              if (isMobile) this.updateVolume();
            })
            .catch((err) => console.warn('[tc-video] ensureAutoPlay failed:', err));
        }
      }
    },

    // Check if player has drifted too far behind live edge and auto-sync if needed
    checkAndSyncToLive() {
      // Skip if paused, reinitializing, or no stream
      if (this.isPaused || this.isReinitializing || !this.streamUrl) {
        return;
      }

      try {
        const videoEl = this.sourceVideo;

        if (videoEl && videoEl.seekable && videoEl.seekable.length > 0) {
          const seekable = videoEl.seekable;
          const liveEdge = seekable.end(seekable.length - 1);
          const currentTime = videoEl.currentTime;
          const drift = liveEdge - currentTime;

          // If drifted more than threshold seconds behind live edge, auto-sync
          if (drift > this.autoSyncThreshold) {
            console.log(`[tc-video] Auto-sync: Drifted ${drift.toFixed(1)}s behind live edge, seeking...`);
            this.seekToLiveEdge();
          }
        }
      } catch (error) {
        // Silently ignore errors in auto-sync check
      }
    },

    // Seek to live edge - syncs playback to the latest available position
    // Call this to reduce latency and sync with other devices
    seekToLiveEdge() {
      // Safety check - don't seek if video not ready or already reinitializing
      if (!this.streamUrl || this.isReinitializing) {
        return;
      }

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isAndroid = /Android/i.test(navigator.userAgent);

      try {
        if (isIOS && this.sourceVideo) {
          // iOS: Use native video seekable range
          const seekable = this.sourceVideo.seekable;
          if (seekable && seekable.length > 0) {
            const liveEdge = seekable.end(seekable.length - 1);
            const currentTime = this.sourceVideo.currentTime;
            const drift = liveEdge - currentTime;
            // Only seek if significantly behind - skip if within buffer (avoids thrashing)
            if (drift <= this.seekBufferSeconds) return;
            // Seek back from live edge for buffer headroom (avoids lag on mobile)
            const targetTime = Math.max(0, liveEdge - this.seekBufferSeconds);
            this.sourceVideo.currentTime = targetTime;
            console.log('[tc-video] iOS seekToLiveEdge:', liveEdge, 'target:', targetTime);
          }
          // Auto-play after seeking (handles pull-to-refresh / visibility change on mobile)
          // With volume ON, browser blocks play - mute first, play, then restore
          if (this.sourceVideo.paused) {
            this.sourceVideo.muted = true;
            this.sourceVideo
              .play()
              .then(() => {
                this.isPaused = false;
                this.updateVolume();
              })
              .catch((err) => console.warn('[tc-video] iOS seekToLiveEdge play failed:', err));
          }
          // If no seekable range on iOS, just skip (don't reinitialize)
        } else if (this.sourceVideo) {
          // VePlayer-backed canvas: seek its underlying video element.
          if (this.sourceVideo.seekable && this.sourceVideo.seekable.length > 0) {
            const seekable = this.sourceVideo.seekable;
            const liveEdge = seekable.end(seekable.length - 1);
            const currentTime = this.sourceVideo.currentTime;
            const drift = liveEdge - currentTime;
            // Only seek if significantly behind - skip if within buffer (avoids thrashing)
            if (drift <= this.seekBufferSeconds) return;
            // Seek back from live edge for buffer headroom (avoids lag on mobile)
            const targetTime = Math.max(0, liveEdge - this.seekBufferSeconds);

            this.sourceVideo.currentTime = targetTime;
            console.log('[tc-video] VePlayer seekToLiveEdge:', liveEdge, '-> seeking to:', targetTime);
          }
          // Auto-play after seeking (handles pull-to-refresh / visibility change on mobile)
          // With volume ON, browser blocks play on Android - mute first, play, then restore
          if (this.sourceVideo.paused) {
            if (isAndroid) this.sourceVideo.muted = true;
            const playPromise = this.sourceVideo.play();
            if (playPromise && typeof playPromise.then === 'function') {
              playPromise
                .then(() => {
                  this.isPaused = false;
                  if (isAndroid) this.updateVolume();
                })
                .catch((err) => console.warn('[tc-video] VePlayer seekToLiveEdge play failed:', err));
            } else {
              this.isPaused = false;
              if (isAndroid) this.updateVolume();
            }
          }
          // If no seekable range, just skip (don't reinitialize - video might still be loading)
        }
      } catch (error) {
        console.warn('[tc-video] seekToLiveEdge error:', error);
        // Don't reinitialize on error - just skip this sync attempt
      }
    },

    // Event handlers (simplified)
    onPlayerPlay(player) {
      // Player play
      this.lastPlayTime = Date.now();
    },
    onPlayerPause(player) {},
    onPlayerEnded(player) {},
    onPlayerLoadeddata(player) {
      this.updateCanvasSize();
    },
    onPlayerWaiting(player) {},
    onPlayerPlaying(player) {},
    onPlayerTimeupdate(player) {},
    onPlayerCanplay(player) {},
    onPlayerCanplaythrough(player) {},
    playerStateChanged(playerCurrentState) {},
  },

  watch: {
    'fight.status': {
      handler(newVal, oldVal) {
        if (this.isAnyPopupOpen) return;
        if (FightStatus[newVal] === FightStatus.started) {
          if (!isMobile && this.$refs.videoPlayerContainer) {
            this.screenScale = 1 + 285 / (this.$refs.videoPlayerContainer.clientHeight + 50);
          } else if (this.isMobileLandscape && this.$refs.videoPlayerContainer) {
            this.screenScale = this.$screen.height / this.$refs.videoPlayerContainer.clientHeight;
          } else {
            this.screenScale = 1;
          }
        } else {
          this.screenScale = 1;
        }
      },
      immediate: true,
    },
    streamUrl: {
      handler(newVal, oldVal) {
        if (!newVal) return;
        console.log('[tc-video] streamUrl changed - Original:', newVal);

        // Reset retry/reinitializing state on any stream URL change
        this.stopRetry();
        this.isReinitializing = false;

        // WebRTC URLs are handled by a different player path.
        if (this.isWebRTCStream) {
          return;
        }

        const normalizedUrl = normalizeStreamUrl(newVal);
        console.log('[tc-video] streamUrl changed - Normalized:', normalizedUrl);

        // Add cache-busting if this is a URL change (not initial load) to force fresh stream
        const isUrlChange = oldVal && oldVal !== newVal;
        const timestamp = Date.now();
        const urlToUse = isUrlChange
          ? normalizedUrl.includes('?')
            ? `${normalizedUrl}&_=${timestamp}`
            : `${normalizedUrl}?_=${timestamp}`
          : normalizedUrl;

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isAndroid = /Android/i.test(navigator.userAgent);

        if (isIOS && this.sourceVideo) {
          this.sourceVideo.src = urlToUse;
          if (isUrlChange) {
            this.sourceVideo.load(); // Force reload on URL change
            // Start playback after load
            this.sourceVideo.play().catch((err) => {
              console.warn('[tc-video] iOS play after URL change failed:', err);
            });
          }
        } else if (this.$refs.vePlayerCanvasContainer) {
          if (this.usingTcPlayerFallback) {
            this.startPlayTcPlayer(urlToUse, !isAndroid);
          } else {
            this.startPlayVePlayer(urlToUse, !isAndroid);
          }
        }
      },
      immediate: true,
    },
    streamDrmData: {
      handler(newVal, oldVal) {
        if (!newVal || !oldVal || !this.vePlayer || !this.streamUrl) return;
        const newLicense = newVal.drmLicenseUrl || '';
        const oldLicense = oldVal.drmLicenseUrl || '';
        if (newLicense && oldLicense && newLicense !== oldLicense && !this.isReinitializing) {
          const isAndroid = /Android/i.test(navigator.userAgent);
          this.startPlayVePlayer(normalizeStreamUrl(this.streamUrl), !isAndroid);
        }
      },
      deep: true,
    },
    'config.language': {
      handler(newVal, oldVal) {
        if (!newVal || !oldVal) return;
        this.getArenaStreamUrl(this.arena.id);
      },
      immediate: true,
    },
    arena: {
      handler(newVal, oldVal) {
        if (!newVal || !oldVal || newVal.id === oldVal.id) return;

        // Reset error/retry state immediately when switching arenas
        this.stopRetry();
        this.isReinitializing = false;
        this.destroyTcPlayer();
        this.usingTcPlayerFallback = false;

        this.getArenaStreamUrl(newVal.id);
      },
      immediate: true,
    },
    // Update robust watermark when member name changes
    memberName: {
      handler(newVal, oldVal) {
        if (!newVal || newVal === oldVal) return;

        // Update robust watermark text
        if (this.robustWatermark) {
          this.robustWatermark.updateConfig({ text: newVal });
        }
      },
      immediate: false,
    },
    streamWatermarkData: {
      handler(newVal, oldVal) {
        if (!newVal) return;

        // Restart watermark frequency control when data changes
        this.stopWatermarkFrequency();
        this.startWatermarkFrequency();

        // Update watermark if type, text, image, movement, or styling changed
        if (
          oldVal &&
          (newVal.watermarkType !== oldVal.watermarkType ||
            newVal.watermark !== oldVal.watermark ||
            newVal.watermarkImageUrl !== oldVal.watermarkImageUrl ||
            newVal.watermarkMovement !== oldVal.watermarkMovement ||
            newVal.watermarkFontSize !== oldVal.watermarkFontSize ||
            newVal.watermarkColor !== oldVal.watermarkColor ||
            newVal.watermarkOpacity !== oldVal.watermarkOpacity)
        ) {
          this.clearOverlays();
          // Always use canvas watermark (works for both iOS and desktop)
          // addText() handles all watermark types including types 3 and 4 for robustWatermark
          this.addText();
        }
      },
      deep: true,
      immediate: false,
    },
  },

  beforeDestroy() {
    // === CRITICAL: Stop animation frame loop FIRST to prevent access to disposed resources ===
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Stop retry mechanism immediately
    this.stopRetry();

    // Clean up Anti-PiP layer
    if (this.antiPipLayer) {
      this.antiPipLayer.destroy();
      this.antiPipLayer = null;
    }

    // Clean up auto-sync interval
    clearTimeout(this.autoSyncStartTimer);
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
      this.autoSyncInterval = null;
    }

    // Clean up controls timer
    this.clearControlsTimer();

    // Remove visibility change listener
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Clean up watermark frequency timer
    this.stopWatermarkFrequency();

    // Clean up CSS watermark if exists
    if (this.watermarkElement) {
      this.watermarkElement.remove();
      this.watermarkElement = null;
    }

    if (this._intMgr) {
      this._intMgr.destroy();
      this._intMgr = null;
    }

    // Clean up robust watermark (dispose releases cached canvas memory)
    if (this.robustWatermark) {
      this.robustWatermark.dispose();
      this.robustWatermark = null;
    }

    // Clean up canvas stream BEFORE disposing player (stops MediaStream tracks)
    this.cleanupCanvasStream();

    // === CRITICAL: Properly release video decoders (iOS has a hard limit of ~4 concurrent decoders) ===
    this.destroyTcPlayer();
    // VePlayer owns its underlying video element and decoder.
    this.destroyVePlayer();

    // Release source video decoder - MUST pause, clear src, and call load() to free hardware decoder
    if (this.sourceVideo) {
      try {
        this.sourceVideo.pause();
        // Remove all event listeners to prevent callbacks after destruction
        this.sourceVideo.onloadedmetadata = null;
        this.sourceVideo.onerror = null;
        this.sourceVideo.onplaying = null;
        // Clear the source to release the hardware video decoder
        this.sourceVideo.removeAttribute('src');
        this.sourceVideo.load(); // Forces iOS Safari to release the decoder
      } catch (e) {
        console.warn('Error releasing sourceVideo decoder:', e);
      }
      if (this.sourceVideo.parentNode) {
        this.sourceVideo.parentNode.removeChild(this.sourceVideo);
      }
      this.sourceVideo = null;
    }

    // Release output video (desktop only)
    if (this.outputVideo) {
      try {
        this.outputVideo.pause();
        this.outputVideo.srcObject = null;
        this.outputVideo.removeAttribute('src');
        this.outputVideo.load();
      } catch (e) {
        console.warn('Error releasing outputVideo:', e);
      }
      if (this.outputVideo.parentNode) {
        this.outputVideo.parentNode.removeChild(this.outputVideo);
      }
      this.outputVideo = null;
    }

    // Clean up canvas context to free GPU memory
    if (this.ctx) {
      this.ctx = null;
    }
    if (this.canvas) {
      this.canvas.width = 0;
      this.canvas.height = 0;
      this.canvas = null;
    }

    // Clean up any remaining video elements with our ID (safety net for leaked elements)
    const remainingVideos = document.querySelectorAll(`#${this.canvasPlayerId}, #${this.canvasPlayerId}_html5_api`);
    remainingVideos.forEach((el) => {
      try {
        if (el.tagName === 'VIDEO') {
          el.pause();
          el.removeAttribute('src');
          el.load();
        }
        el.remove();
      } catch (e) {
        // Ignore cleanup errors
      }
    });
  },
};
</script>

<style lang="scss">
// CSS for canvas-based video streaming with PiP support
.tc-video-player-box {
  min-height: 200px;
  position: relative;
  z-index: 1002;
  transform-origin: left top;
  transition: all 1s ease;

  &:before {
    content: '';
    background-image: url('~@/assets/images/shine.png');
    height: 30px;
    width: 100%;
    z-index: 2;
    bottom: 0;
    left: 50%;
    display: block;
    position: absolute;
    transform: translateX(-50%);
    background-size: cover;
    pointer-events: none;
  }

  .video-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16/9;
    background-color: #0d1a24;
    // Prevent iOS pull-to-refresh on video container
    overscroll-behavior: none;
    -webkit-overflow-scrolling: auto;
    touch-action: manipulation;

    @supports not (aspect-ratio: 16/9) {
      &::before {
        float: left;
        padding-top: 56.25%;
        content: '';
      }

      &::after {
        display: block;
        content: '';
        clear: both;
      }
    }
  }

  .ve-player-canvas-source {
    position: fixed;
    top: 0;
    left: 0;
    width: 2px;
    height: 2px;
    opacity: 0;
    pointer-events: none;
    z-index: -1;
    overflow: hidden;
  }

  // Play/Pause controls
  .video-controls {
    position: absolute;
    bottom: 20px;
    right: 20px;
    z-index: 1002;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    display: flex;
    gap: 12px;
    pointer-events: none;

    &.controls-visible {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }
  }

  // Desktop: show on hover as well
  @media (hover: hover) and (pointer: fine) {
    .video-wrapper:hover .video-controls {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }
  }

  // Dimmed overlay when paused (clickable to resume)
  .video-paused-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 1001;
    cursor: pointer;
  }

  .control-btn {
    background-color: rgba(0, 0, 0, 0.7);
    border: 2px solid rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    padding: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    color: white;

    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
      border-color: white;
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }

    svg {
      display: block;
    }
  }

  .pause-btn,
  .play-btn {
    width: 48px;
    height: 48px;
  }

  // Volume control styling
  .volume-control {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .volume-btn {
    width: 48px;
    height: 48px;
  }

  .volume-slider-container {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background-color: rgba(0, 0, 0, 0.8);
    border-radius: 20px;
    animation: slideIn 0.2s ease;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .volume-slider {
    width: 100px;
    height: 4px;
    -webkit-appearance: none;
    appearance: none;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    outline: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: white;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-top: -5px; // Center 14px thumb on 4px track: (14-4)/2 = 5px

      &:hover {
        transform: scale(1.2);
        background: #2483d5;
      }
    }

    &::-moz-range-thumb {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: white;
      cursor: pointer;
      border: none;
      transition: all 0.2s ease;

      &:hover {
        transform: scale(1.2);
        background: #2483d5;
      }
    }

    &::-webkit-slider-runnable-track {
      height: 4px;
      border-radius: 2px;
      background: linear-gradient(
        to right,
        #2483d5 0%,
        #2483d5 var(--volume-percent, 0%),
        rgba(255, 255, 255, 0.3) var(--volume-percent, 0%),
        rgba(255, 255, 255, 0.3) 100%
      );
    }

    &::-moz-range-track {
      height: 4px;
      border-radius: 2px;
      background: rgba(255, 255, 255, 0.3);
    }

    &::-moz-range-progress {
      height: 4px;
      border-radius: 2px;
      background: #2483d5;
    }
  }

  .volume-value {
    color: white;
    font-size: 12px;
    font-weight: 500;
    min-width: 35px;
    text-align: right;
    user-select: none;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .video-canvas {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    cursor: pointer;
    background-color: #0d1a24;

    &.ios-canvas {
      display: block !important; // Force visible on iOS
      z-index: 1;
      pointer-events: auto;
    }

    &.android-canvas {
      display: block !important; // Force visible on Android
      z-index: 1;
      pointer-events: auto;
    }
  }

  // Output video styles (dynamically created)
  #outputVideo {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    cursor: pointer;
    background-color: #0d1a24;
    object-fit: cover;
  }

  // Direct video styles (for iOS/unsupported browsers)
  .direct-video {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    background-color: #0d1a24;
    z-index: 1;
  }

  // iOS native video (uses native HLS support)
  .ios-native-video {
    width: 100% !important;
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    background-color: #0d1a24;
    z-index: 1;

    // Ensure native iOS controls are visible and styled
    &::-webkit-media-controls-panel {
      display: flex !important;
    }

    &::-webkit-media-controls-play-button,
    &::-webkit-media-controls-start-playback-button {
      display: flex !important;
    }
  }

  // CSS watermark styles (for iOS/direct mode)
  .css-watermark {
    position: absolute;
    color: #ff0000;
    font-size: 30px;
    font-family: Arial, sans-serif;
    font-weight: bold;
    z-index: 1001;
    pointer-events: none;
    white-space: nowrap;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
    transition: opacity 0.3s ease;
    user-select: none;
    -webkit-user-select: none;
  }

  .overlay-controls {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1000;
    display: flex;
    gap: 8px;

    button {
      padding: 4px 8px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      border: 1px solid #333;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;

      &:hover {
        background: rgba(0, 0, 0, 0.9);
      }
    }
  }

  .no-active-arena {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: #0d1a24;
    aspect-ratio: 16/9;

    &.match-day-ending{
      background: url('~@/assets/images/ended.webp') no-repeat center center;
      background-size: contain;
    }

    img {
      height: 6.4rem;
      width: 6.4rem;
    }

    .text {
      text-align: center;
      font-size: 16px;
      color: #b2d6ea;

      &.my-3 {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
    }
  }

  .video-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16/9;
    background-color: #0d1a24;

    .text {
      text-align: center;

      &.my-3 {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
    }

    @supports not (aspect-ratio: 16/9) {
      &::before {
        float: left;
        padding-top: 56.25%;
        /* 16:9 aspect ratio */
        content: '';
      }

      &::after {
        display: block;
        content: '';
        clear: both;
      }
    }
  }

  /*排序顺序*/
  .video-js {
    font-family: 'PingFang SC', 'Helvetica Neue', 'Hiragino Sans GB', 'Segoe UI', 'Microsoft YaHei', '微软雅黑',
      sans-serif;
    width: 100%;

    .vjs-menu-button-inline.vjs-slider-active {
      width: 100px;
    }

    .vjs-menu-button-inline {
      &:focus {
        width: 100px;
      }

      &:hover {
        width: 100px;
      }

      &:before {
        width: 15px;
      }
    }

    .vjs-big-play-button {
      display: none !important;
    }

    // .vjs-control {
    //   width: 50px;
    // }

    .vjs-control.vjs-live-control {
      width: auto;
      padding-left: 5px;
      letter-spacing: 1px;
    }

    .vjs-load-progress {
      div {
        display: none !important;
      }
    }

    .vjs-mouse-display {
      &:after {
        padding: 0 0.4em 3px;
        border-radius: 0;
        font-size: 10px;
        padding: 0;
        width: 30px;
        height: 15px;
        line-height: 15px;
        top: -30px;
      }
    }

    .vjs-play-progress {
      &:after {
        padding: 0 0.4em 3px;
        border-radius: 0;
        font-size: 10px;
        padding: 0;
        width: 30px;
        height: 15px;
        line-height: 15px;
        top: -30px;
      }

      background-color: #2483d5;

      &:before {
        top: -3px;
      }
    }

    .vjs-big-play-button {
      top: 50% !important;
      left: 50% !important;
      margin-left: -15px;
      margin-top: -10px;
      background-color: rgba(0, 0, 0, 0.45);
      font-size: 35px;
      height: 20px !important;
      line-height: 20px !important;
      margin-top: -10px !important;

      &:focus {
        background-color: rgba(36, 131, 213, 0.9);
      }

      &:active {
        background-color: rgba(36, 131, 213, 0.9);
      }
    }

    .vjs-loading-spinner {
      border-color: rgba(36, 131, 213, 0.8);
    }

    .vjs-control-bar2 {
      background-color: #000000;
    }

    .vjs-control-bar {
      color: #ffffff;
      font-size: 14px;

      .vjs-play-control {
        order: 0;
      }

      .vjs-time-control {
        display: none;
      }

      .vjs-playback-rate {
        .vjs-playback-rate-value {
          font-size: 12px;
          line-height: 2.4;
        }

        order: 5;
      }

      .vjs-volume-menu-button {
        order: 3;
      }

      .vjs-resolution-button {
        order: 4;

        .vjs-resolution-button-label {
          display: block;
          line-height: 30px;
        }
      }

      .vjs-fullscreen-control {
        order: 6;
      }
    }

    .vjs-volume-level {
      background-color: #2483d5;
    }

    .vjs-progress-control {
      &:hover {
        .vjs-progress-holder {
          font-size: 1.3px;
        }
      }
    }

    .vjs-menu {
      li {
        padding: 0;
        line-height: 20px;
        font-size: 1.1px;
        font-family: 'PingFang SC', 'Helvetica Neue', 'Hiragino Sans GB', 'Segoe UI', 'Microsoft YaHei', '微软雅黑',
          sans-serif;
      }
    }

    .vjs-time-tooltip {
      border-radius: 0;
      font-size: 10px;
      padding: 0;
      width: 30px;
      height: 15px;
      line-height: 15px;
      top: -30px;
    }

    .vjs-menu-button-popup {
      .vjs-menu {
        width: 50px;
        left: -10px;
      }
    }

    .vjs-menu-button-popup.vjs-volume-menu-button-vertical {
      .vjs-menu {
        left: 0;
      }
    }

    .vjs-progress-control.vjs-control {
      order: 2;
    }
  }

  .video-js.vjs-no-flex {
    .vjs-menu-button-inline {
      width: 100px;
    }
  }

  .vjs-menu-button-inline {
    .vjs-menu {
      left: 30px;
    }
  }

  .vjs-paused.vjs-has-started.vjs-custom-skin {
    > .video-js {
      .vjs-big-play-button {
        display: block;
        display: block;
      }
    }
  }

  .video-js.vjs-ended {
    .vjs-big-play-button {
      display: block;
      display: block !important;
      display: block;
    }

    .vjs-loading-spinner {
      display: none;
    }
  }

  .video-js.vjs-paused {
    .vjs-big-play-button {
      display: block;
      display: block;
    }
  }

  .vjs-seeking {
    .vjs-big-play-button {
      display: none !important;
    }
  }

  .vjs-waiting {
    .vjs-big-play-button {
      display: none !important;
    }
  }

  .video-js {
    &:hover {
      .vjs-big-play-button {
        background-color: rgba(36, 131, 213, 0.9);
      }
    }
  }

  .vjs-menu-button-popup.vjs-volume-menu-button-vertical {
    .vjs-menu {
      left: 0;
    }
  }

  .vjs-button,
  .video-js button {
    cursor: pointer;

    &:before {
      font-size: 20px;
    }
  }

  .vjs-custom-update-button {
    cursor: pointer;
    font-family: 'VJS Material Icons', serif;
    font-weight: normal;
    font-style: normal;

    &:before {
      content: '\e923'; // this relates to a specific icon
      font-size: 20px;
      line-height: 1.67;
    }

    &.disabled:before {
      content: '\e075'; // this relates to a specific icon
      font-size: 20px;
      line-height: 1.67;
    }
  }

  .vjs-custom-resolution-button {
    cursor: pointer;

    .vjs-control-text {
      height: 42px;
      width: 42px;
      display: block;
      clip: unset;
      line-height: 42px;
      position: absolute;
      top: 0;
    }
  }

  .vjs-custom-arena-button {
    position: absolute !important;
    right: 10px;
    bottom: 50px;
    cursor: pointer;
    width: 90px !important;
    height: 30px !important;
    padding: 4px 8px !important;
    border-radius: 4px;
    border: solid 1.5px #fff !important;
    background-color: #e4e7eb !important;

    .vjs-control-text {
      clip: unset;
      height: 30px;
      width: 100%;
      display: block;
      color: #1a1d22;
      line-height: 30px;
      top: 0;
      text-transform: uppercase;
      left: 50%;
      transform: translateX(-50%);
    }

    &.active {
      background-color: #08172e !important;

      .vjs-control-text {
        color: #fff;
      }
    }

    &.arena-1 {
      bottom: 50px;
    }

    &.arena-2 {
      bottom: 90px;
    }

    &.arena-3 {
      bottom: 130px;
    }

    &.arena-4 {
      bottom: 170px;
    }

    &.arena-5 {
      bottom: 210px;
    }
  }

  .video-banner-wrapper {
    display: flex;
    justify-content: space-between;
    padding: 0 24px;
    height: 48px;
    box-sizing: border-box;
    align-items: center;
    background-image: url('~@/assets/svg/video-banner.svg');
    background-position: center;
    background-size: cover;
    font-size: 12px;
    font-weight: 600;
    line-height: 40px;
    color: #fafafa;
    text-transform: uppercase;

    &.is-mobile {
      display: none;
    }

    .odds-wrapper {
      display: flex;
      align-items: center;

      .odds {
        font-size: 22px;
        color: #fff;
        text-transform: none;
        user-select: none;
      }
    }

    &.reverse {
      flex-direction: row-reverse;
      background-image: url('~@/assets/svg/video-banner-reverse.svg');
    }
  }
}

// Global CSS for PiP support - removed blocking styles
</style>
